/**
 * BooksClose — closing the books, once a month (Matt, 2026-10-28). docs/BOOKKEEPING.md "The monthly close".
 *
 * Matt, on the old four-step screen: "It feels like something someone made in FileMaker Pro back in
 * 2001 ... every time I go to use it I am going to forget what these inputs are for." He closes the
 * books himself, once a month. So the screen opens on one sentence (how current the books are) and
 * one button (close the next month), and the close walks him through:
 *
 *   Bring  — the downloads, each named with where to get it and the dates; each checks itself
 *   Decide — one card at a time, the suggestion filled in, accounts by name
 *   Post   — what goes into Core, by account name, behind the one red button
 *   Done   — ties to the bank, books current through, the CPA package, the set-asides
 *
 * The engine is the old one (commitRows, commitDeposits, writeFundTransfer, with all their guards).
 * The old screen stays reachable as "Look up and fix" for the rare deep repair.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getCloseStatus, confirmNoActivity, getClosePlan, getCloseTie, getCloseRegister, getClosePayments, markInvoice, writeOffInvoice, getCloseReimbursables, markExpenseNotBillable, getSetAsidePreview, getReserves, moveReserve, finishClose, getClosePackage,
  saveSetAsideTargets, importBank, importPayroll, reclassify, listBatches, applyToVendor, updateRow, setProjectSplit, addCoreVendor, phaseListOk,
  linkReimbToRow, linkExpenseToRow, commitRows, commitDeposits, writeFundTransfer, setMoneyIn, writeDepositWithLines, writeRetainerPayment, getCloseStatements, saveStatement, uploadStatement, getChartOfAccounts, getExpenseItems, askAccount,
} from '../../services/financeImport.js'
import { getCpaCenter, cpaAction, addCpaFile, getCpaOwed } from '../../services/financeImport.js'
import { confirmDialog, alertDialog, promptDialog } from '../Dialog.jsx'

const money = (n) => (Number(n) < 0 ? '−' : '') + '$' + Math.abs(Number(n) || 0)
  .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const longDate = (iso) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }) : ''
const shortDate = (iso) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : ''
const fullDate = (iso) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00Z')
  .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : ''
const monthOnly = (name) => String(name || '').split(' ')[0]
// A date as typed into Core's report filters (MM/DD/YYYY).
const coreDate = (iso) => { const [y, m, d] = String(iso).slice(0, 10).split('-'); return `${m}/${d}/${y}` }
// The exact dates to set in Core's Reports for one of the CPA's four reports (Matt, 2026-11-04): the
// Balance Sheet is a point in time (as of the period's last day); the other three cover the period.
const coreReportDates = (title, p) => /balance sheet/i.test(title)
  ? 'As of ' + coreDate(p.end)
  : 'From ' + coreDate(p.start) + ' to ' + coreDate(p.end)

const C = {
  page: { fontFamily: '"DM Sans", sans-serif', color: 'var(--aj-text)', padding: '28px 36px 80px', maxWidth: 1180, margin: '0 auto' },
  card: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '22px 26px' },
  h: { fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 11.5, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  muted: { color: 'var(--aj-text-mid)' },
  mono: { fontFamily: '"DM Mono", monospace' },
  big: { fontSize: 15, fontWeight: 600, padding: '13px 24px', borderRadius: 8, border: 'none', background: 'var(--aj-accent)', color: '#fff', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif' },
  quiet: { fontSize: 13, padding: '8px 14px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif' },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--aj-accent-blue)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' },
  danger: { fontSize: 15, fontWeight: 600, padding: '13px 24px', borderRadius: 8, border: 'none', background: '#b03a18', color: '#fff', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif' },
  good: '#2e7d4f', bad: '#b03a18',
}

// Decision tiles, the TriageTiles look (Matt's standing convention for decisions, 2026-10-16).
const TILE_CSS = `
.aj-bc-grid{display:grid;gap:10px}
.aj-bc-tile{font:inherit;display:flex;align-items:flex-end;min-height:96px;padding:16px;border-radius:10px;border:1px solid var(--aj-border-mid);background:var(--aj-bg-surface);color:var(--aj-text);cursor:pointer;text-align:left;transition:transform .16s ease,box-shadow .16s ease,border-color .16s ease}
.aj-bc-tile:hover:not(:disabled){transform:translateY(-3px);box-shadow:0 10px 22px rgba(7,39,45,0.14);border-color:var(--aj-text)}
.aj-bc-tile:focus-visible{outline:2px solid #b8540a;outline-offset:2px}
.aj-bc-tile:disabled{opacity:.45;cursor:default}
.aj-bc-tile.aj-bc-primary{background:#07272D;border-color:#07272D;color:#fff}
.aj-bc-lab{display:block;font-family:"DM Sans",sans-serif;font-size:15px;font-weight:600;line-height:1.25}
.aj-bc-sub{display:block;font-family:"DM Sans",sans-serif;font-size:12.5px;line-height:1.35;opacity:.75;margin-top:4px}
`

function Steps({ at }) {
  const list = [['bring', 'Downloads'], ['decide', 'Decide'], ['post', 'Post'], ['done', 'Done']]
  const idx = list.findIndex(x => x[0] === at)
  return (
    <div style={{ display: 'flex', gap: 8, ...C.mono, fontSize: 12.5, color: 'var(--aj-text-mid)' }}>
      {list.map(([k, label], i) => (
        <span key={k} style={{ color: i === idx ? 'var(--aj-text)' : undefined, fontWeight: i === idx ? 600 : 400 }}>
          {label}{i < idx ? ' ✓' : ''}{i < list.length - 1 ? '  ·' : ''}
        </span>
      ))}
    </div>
  )
}

export default function BooksClose({ onLookup, ProjectCoding, ConnectPayee, S }) {
  const [status, setStatus] = useState(null)
  const [view, setView] = useState('home')
  const [err, setErr] = useState('')
  const [done, setDone] = useState(null)

  const refresh = useCallback(async () => {
    try { setErr(''); setStatus(await getCloseStatus()) } catch (e) { setErr(e.message) }
  }, [])
  useEffect(() => { refresh() }, [refresh])

  if (!status) {
    return <div style={C.page}>{err ? <div style={{ color: C.bad }}>{err}</div> : <div style={C.muted}>Reading the books…</div>}</div>
  }
  const m = status.month
  return (
    <div style={C.page}>
      <style>{TILE_CSS}</style>
      {err && <div style={{ ...C.card, borderColor: C.bad, color: C.bad, marginBottom: 16 }}>{err}</div>}
      {view === 'home' && <Home status={status} onClose={() => setView('bring')} onFinish={() => setView('post')} onLookup={onLookup} onSaved={refresh} />}
      {view === 'bring' && <Bring status={status} refresh={refresh} onBack={() => setView('home')} onNext={() => setView('decide')} />}
      {view === 'decide' && <Decide month={m} onBack={() => setView('bring')} onNext={() => setView('post')} onLookup={onLookup} ProjectCoding={ProjectCoding} ConnectPayee={ConnectPayee} S={S} />}
      {view === 'post' && <Post month={m} onBack={() => setView('decide')} onClosed={(r) => { setDone(r); setView('done'); refresh(); window.dispatchEvent(new CustomEvent('books-closed')) }} />}
      {view === 'done' && done && <Done result={done} onHome={() => setView('home')} />}
    </div>
  )
}

// ── home ─────────────────────────────────────────────────────────────────────

function Home({ status, onClose, onFinish, onLookup, onSaved }) {
  const m = status.month
  const [targets, setTargets] = useState(false)
  // Where this month's close stands (Matt, 2026-11-02: he had posted everything and the screen still
  // just said "Close September", with no way to tell what was left). Each step says done or what's left,
  // and once everything is in Core the button goes straight to the balances and the Close button.
  const pg = status.progress || null
  const posted = !!pg && status.downloadsReady && pg.rows > 0 && pg.waiting === 0
  const stepsLeft = pg && m.over ? [
    { label: 'Bring the downloads', done: status.downloadsReady,
      left: `${status.accounts.filter(a => !a.ok).length + (status.payroll && !status.payroll.ok ? 1 : 0)} still to bring` },
    { label: 'Decide and post to Core', done: posted,
      left: pg.rows ? `${pg.waiting} of ${pg.rows} transactions not in Core yet` : 'After the downloads' },
    { label: 'Type the bank statement balances', done: pg.statementsEntered >= pg.statementsNeeded,
      left: `${pg.statementsEntered} of ${pg.statementsNeeded} accounts typed` },
    { label: `Check it ties to the bank, then close ${monthOnly(m.name)}`, done: false, left: 'The last button' },
  ] : null
  return (
    <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ ...C.card, padding: '32px 36px' }}>
          <div style={{ fontSize: 15, ...C.muted }}>Books are current through</div>
          <div style={{ fontSize: 40, fontWeight: 600, lineHeight: 1.15, margin: '6px 0 12px' }}>{fullDate(status.closedThrough)}</div>
          <div style={{ fontSize: 16, lineHeight: 1.55, ...C.muted }}>
            {m.over
              ? <>{m.name} closes on <b style={{ color: 'var(--aj-text)' }}>{longDate(m.due)}</b>. Your downloads, a few decisions, one button.</>
              : <>{monthOnly(m.name)} ends {longDate(m.end)}. It closes on <b style={{ color: 'var(--aj-text)' }}>{longDate(m.due)}</b>; you can bring downloads before then.</>}
            {m.over && status.today > m.due && <span style={{ color: C.bad }}> It's past due.</span>}
          </div>
          {stepsLeft && (
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {stepsLeft.map((x, i) => {
                const next = !x.done && stepsLeft.slice(0, i).every(y => y.done)
                return (
                  <div key={x.label} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 14.5 }}>
                    <span style={{ width: 18, flex: '0 0 18px', color: x.done ? C.good : next ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontWeight: 600 }}>{x.done ? '✓' : next ? '→' : '·'}</span>
                    <span style={{ fontWeight: next ? 600 : 400, color: x.done ? 'var(--aj-text-mid)' : 'var(--aj-text)' }}>{x.label}</span>
                    {!x.done && <span style={{ fontSize: 13, ...C.muted }}>{x.left}</span>}
                  </div>
                )
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 20, flexWrap: 'wrap' }}>
            {posted
              ? <button style={C.big} onClick={onFinish}>Check the balances and close {monthOnly(m.name)}</button>
              : <button style={C.big} onClick={onClose}>{stepsLeft && status.downloadsReady ? `Continue ${monthOnly(m.name)}` : `Close ${monthOnly(m.name)}`}</button>}
            {!posted && status.accounts.some(a => a.files > 1 || a.ok) && (
              <span style={{ fontSize: 13, ...C.muted }}>Your downloads and decisions so far are saved. It picks up where you left off.</span>
            )}
          </div>
        </div>

        <ReservesCard />

        <div style={C.card}>
          <div style={{ ...C.h, marginBottom: 12 }}>Recent months</div>
          {status.history.map(h => <MonthLine key={h.month} h={h} />)}
        </div>

        <CpaCenterCard />

        <div style={{ display: 'flex', gap: 22, fontSize: 13.5, flexWrap: 'wrap' }}>
          <button style={C.link} onClick={() => setTargets(v => !v)}>Set aside targets</button>
          <button style={C.link} onClick={onLookup}>Look up and fix a transaction</button>
        </div>
        {targets && <Targets value={status.targets} onSaved={() => { setTargets(false); onSaved() }} />}
      </div>

      <div style={{ ...C.card, flex: '0 1 380px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={C.h}>What the firm owes, and when</div>
        {status.obligations.map((o, i) => (
          <div key={i} style={{ paddingBottom: 10, borderBottom: '0.5px solid var(--aj-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{o.name}</span>
              <span style={{ ...C.mono, fontSize: 12.5, whiteSpace: 'nowrap' }}>{shortDate(o.date)}</span>
            </div>
            {o.note && <div style={{ fontSize: 12.5, ...C.muted, marginTop: 2 }}>{o.note}</div>}
          </div>
        ))}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>1% for the Planet</span>
            <span style={{ ...C.mono, fontSize: 12.5 }}>Monthly</span>
          </div>
          <div style={{ fontSize: 12.5, ...C.muted, marginTop: 2 }}>Set aside with each close, from payments collected.</div>
        </div>
      </div>
    </div>
  )
}

function MonthLine({ h }) {
  const [busy, setBusy] = useState(false)
  const download = async () => {
    setBusy(true)
    try { downloadPackage(await getClosePackage(h.month)) } catch (e) { alertDialog({ title: 'Could not prepare it', message: e.message }) } finally { setBusy(false) }
  }
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600, minWidth: 140 }}>{h.name}</span>
      <span style={{ fontSize: 13, ...C.muted, flex: 1 }}>
        {h.seeded ? 'Caught up before the monthly close existed.'
          : <>Closed {shortDate(h.closedAt)} · {h.ties ? <span style={{ color: C.good }}>ties to the bank ✓</span> : <span>closed with a note: {h.explanation}</span>}</>}
      </span>
      {!h.seeded && <button style={C.link} disabled={busy} onClick={download}>{busy ? 'Preparing…' : 'CPA package'}</button>}
    </div>
  )
}

function Targets({ value, onSaved }) {
  const [v, setV] = useState({ ...(value || {}) })
  const [err, setErr] = useState('')
  const rows = [
    ['taxPctOfProfit', "Owners' taxes", '% of profit, split 50/50'],
    ['planetPctOfCollected', '1% for the Planet', '% of payments collected'],
    ['savingsPctOfCollected', 'Savings reserve', '% of payments collected'],
    ['savingsGoalMonths', 'Savings goal', 'months of running costs'],
    ['bonusPctOfProfit', 'Bonus fund', '% of profit before owner distributions'],
    ['investmentPctOfProfit', 'Investment account', '% of profit (0 until it exists)'],
  ]
  return (
    <div style={C.card}>
      <div style={{ ...C.h, marginBottom: 12 }}>Set aside from each month</div>
      {rows.map(([k, label, unit]) => (
        <label key={k} style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0', fontSize: 14 }}>
          <span style={{ minWidth: 170 }}>{label}</span>
          <input type="number" min="0" max="100" step="0.5" value={v[k] ?? ''} onChange={e => setV({ ...v, [k]: e.target.value })}
            style={{ width: 80, ...C.mono, fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }} />
          <span style={{ fontSize: 13, ...C.muted }}>{unit}</span>
        </label>
      ))}
      {err && <div style={{ color: C.bad, fontSize: 13 }}>{err}</div>}
      <button style={{ ...C.quiet, marginTop: 8 }} onClick={async () => {
        try { await saveSetAsideTargets(v); onSaved() } catch (e) { setErr(e.message) }
      }}>Save targets</button>
    </div>
  )
}

// ── bring the downloads ─────────────────────────────────────────────────────

function Bring({ status, refresh, onBack, onNext }) {
  const m = status.month
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Header title={`Close ${m.name}`} step="bring" onBack={onBack} backLabel="Books" />
      <div>
        <div style={{ fontSize: 26, fontWeight: 600 }}>Bring the downloads</div>
        <div style={{ fontSize: 15, ...C.muted, marginTop: 6, lineHeight: 1.5 }}>
          One file on each card. Each from the date shown to today: starting early catches anything that posted late across the month end, and what's already in is set aside on its own.
        </div>
      </div>

      {status.accounts.map(a => <AccountCard key={a.code} a={a} month={m} refresh={refresh} />)}
      <PayrollCard p={status.payroll} month={m} refresh={refresh} />

      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <button style={{ ...C.big, opacity: status.downloadsReady ? 1 : 0.4 }} disabled={!status.downloadsReady} onClick={onNext}>Continue</button>
        <span style={{ fontSize: 13, ...C.muted }}>
          {status.downloadsReady ? 'Everything the month needs is in.' : 'Continue opens once every card above is checked.'}
        </span>
      </div>
    </div>
  )
}

function Tick({ ok }) {
  return (
    <div style={{ width: 34, height: 34, borderRadius: 17, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: ok ? C.good : 'transparent', border: ok ? 'none' : '1.5px solid var(--aj-border-mid)' }}>
      {ok && <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4L19 7" /></svg>}
    </div>
  )
}

/**
 * One card per file (Matt, 2026-10-29: "It feels fragile to upload everything into one uploader").
 * The card says what the file is, so nothing is guessed from a file name, and whatever happens to
 * the file (read, refused, why) is said right on its card. Drop the file on the card or choose it.
 */
function FileCard({ ok, title, sub, children, onFile, aside }) {
  const ref = useRef(null)
  const [drag, setDrag] = useState(false)
  const [busy, setBusy] = useState('')
  const [result, setResult] = useState(null)   // { ok, say }
  async function take(files) {
    const f = Array.from(files || [])[0]
    if (!f) return
    setBusy('Reading ' + f.name + '…'); setResult(null)
    try { setResult(await onFile(f)) }
    catch (e) { setResult({ ok: false, say: e.message || String(e) }) }
    finally { setBusy('') }
  }
  return (
    <div
      onDragOver={e => { e.preventDefault(); setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files) }}
      style={{ ...C.card, borderStyle: drag ? 'dashed' : 'solid', borderColor: drag ? 'var(--aj-accent)' : ok ? 'rgba(46,125,79,0.45)' : 'var(--aj-border)', background: drag ? 'var(--aj-bg-subtle)' : 'var(--aj-bg-surface)' }}>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
        <Tick ok={ok} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{title} {sub && <span style={{ ...C.mono, fontSize: 13, ...C.muted }}>{sub}</span>}</div>
          <div style={{ fontSize: 14, marginTop: 3, color: ok ? C.good : 'var(--aj-text-mid)', lineHeight: 1.45 }}>{children}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flex: 'none' }}>
          <button style={{ ...C.quiet, ...(ok ? {} : { borderColor: 'var(--aj-accent)' }) }} disabled={!!busy} onClick={() => ref.current && ref.current.click()}>
            {busy ? 'Reading…' : ok ? 'Add another file' : 'Choose the file'}
          </button>
          {aside}
        </div>
        <input ref={ref} type="file" style={{ display: 'none' }} onChange={e => { const f = Array.from(e.target.files || []); e.target.value = ''; take(f) }}   /* copy first: clearing the input empties its live file list */ />
      </div>
      {(busy || result) && (
        <div style={{ marginTop: 12, marginLeft: 52, fontSize: 13.5, lineHeight: 1.5, color: busy ? 'var(--aj-text-mid)' : result.ok ? C.good : C.bad }}>
          {busy || ((result.ok ? '✓ ' : '! ') + result.say)}
        </div>
      )}
      {!busy && !result && !ok && <div style={{ marginTop: 8, marginLeft: 52, fontSize: 12.5, ...C.muted }}>Or drop the file on this card.</div>}
    </div>
  )
}

function AccountCard({ a, month, refresh }) {
  const [busy, setBusy] = useState(false)
  const onFile = async (f) => {
    const r = await importBank(await f.text(), f.name, a.code)
    await refresh()
    return { ok: r.balanceOk !== false,
      say: `${shortDate(r.periodStart)} to ${shortDate(r.periodEnd)}: ${r.rows} new transaction${r.rows === 1 ? '' : 's'}`
        + (r.duplicatesSkipped ? `, ${r.duplicatesSkipped} already in from an earlier download` : '')
        + (r.balanceOk === false ? '. The running balance breaks inside this file, so rows may be missing. Download it again.' : '.') }
  }
  return (
    <FileCard ok={a.ok} title={a.name} sub={'••' + a.last4} onFile={onFile}
      aside={!a.ok && a.canConfirm && (
        <button style={C.link} disabled={busy} onClick={async () => {
          if (!(await confirmDialog({ title: `No activity on ${a.name} since ${shortDate(a.coveredThrough)}?`,
            message: `Chase shows no transactions on ${a.name} ••${a.last4} after ${fullDate(a.coveredThrough)} through the end of ${month.name}. This is recorded with the close.`,
            confirmLabel: 'Yes, none' }))) return
          setBusy(true); try { await confirmNoActivity(a.code, a.coveredThrough); await refresh() } finally { setBusy(false) }
        }}>No activity since {shortDate(a.coveredThrough)}</button>
      )}>
      {a.ok ? a.say : <>{a.say} <span style={{ color: 'var(--aj-text)' }}>chase.com › {a.name.replace('Chase ', '')} ••{a.last4} › Download account activity › CSV, <b style={C.mono}>{shortDate(a.downloadFrom)}</b> to today.</span></>}
    </FileCard>
  )
}

function PayrollCard({ p, month, refresh }) {
  const onFile = async (f) => {
    const r = await importPayroll(await f.text(), f.name)
    // Payroll is what tells pay apart from owner draws, so the month's bank downloads are re-read against it.
    try { const b = await listBatches(); for (const x of (b.batches || []).filter(x => x.period_end >= month.first)) await reclassify(x.id) } catch { /* the plan re-reads anyway */ }
    await refresh()
    return { ok: true, say: `${r.lines} lines across ${r.paydays} pay day${r.paydays === 1 ? '' : 's'}.` }
  }
  return (
    <FileCard ok={p.ok} title="Gusto payroll journal" onFile={onFile}>
      {p.ok ? p.say : <>{p.say} <span style={{ color: 'var(--aj-text)' }}>Gusto › Reports › Payroll journal, <b style={C.mono}>{shortDate(p.downloadFrom && p.downloadFrom < month.first ? p.downloadFrom : month.first)}</b> to <b style={C.mono}>{shortDate(p.downloadTo || month.end)}</b>, CSV.</span></>}
    </FileCard>
  )
}

function Header({ title, step, onBack, backLabel }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', paddingBottom: 14, borderBottom: '0.5px solid var(--aj-border)' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
        <button style={C.link} onClick={onBack}>‹ {backLabel}</button>
        <span style={{ ...C.h, fontSize: 13 }}>{title}</span>
      </div>
      <Steps at={step} />
    </div>
  )
}

// ── decide ──────────────────────────────────────────────────────────────────

function Decide({ month, onBack, onNext, onLookup, ProjectCoding, ConnectPayee, S }) {
  const [plan, setPlan] = useState(null)
  const [err, setErr] = useState('')
  const [i, setI] = useState(0)
  const [gone, setGone] = useState(() => new Set())
  const [chart, setChart] = useState([])
  const [items, setItems] = useState([])

  const load = useCallback(async () => {
    setErr(''); setPlan(null); setGone(new Set()); setI(0)
    try { setPlan(await getClosePlan()) } catch (e) { setErr(e.message) }
  }, [])
  useEffect(() => { load() }, [load])
  useEffect(() => { getChartOfAccounts().then(r => setChart(r.accounts || [])).catch(() => setChart([])) }, [])
  useEffect(() => { getExpenseItems().then(r => setItems(r.items || [])).catch(() => setItems([])) }, [])

  const cards = useMemo(() => ((plan && plan.cards) || []).filter(c => !gone.has(c.key)), [plan, gone])
  const card = cards[Math.min(i, Math.max(0, cards.length - 1))]
  const settle = (key) => setGone(prev => new Set(prev).add(key))
  const saved = gone.size

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Header title={`Close ${month.name}`} step="decide" onBack={onBack} backLabel="Downloads" />
      {err && <div style={{ color: C.bad }}>{err} <button style={C.link} onClick={load}>Try again</button></div>}
      {!plan && !err && <div style={{ fontSize: 15, ...C.muted }}>Checking every transaction against Core… this takes about half a minute.</div>}
      {plan && (
        <>
          <div style={{ fontSize: 15, ...C.muted }}>
            {plan.rowCount} transactions in {month.name}{plan.lateCount ? `, plus ${plan.lateCount} late arrival${plan.lateCount === 1 ? '' : 's'} for earlier months` : ''}.{' '}
            {cards.length ? <b style={{ color: 'var(--aj-text)' }}>{cards.length} need{cards.length === 1 ? 's' : ''} you.</b> : 'Nothing needs you.'}
          </div>
          {saved > 0 && (
            <div style={{ fontSize: 13.5, color: C.good }}>
              ✓ {saved} decision{saved === 1 ? '' : 's'} saved. {saved === 1 ? 'It goes' : 'They go'} into Core when you post {monthOnly(month.name)}; closing the window loses nothing.
            </div>
          )}
          {card ? (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 560px', minWidth: 0 }}>
                <DecisionCard key={card.key} card={card} n={cards.indexOf(card) + 1} of={cards.length} chart={chart}
                  onDone={() => settle(card.key)} onLookup={onLookup} onRecheck={load} items={items}
                  ProjectCoding={ProjectCoding} ConnectPayee={ConnectPayee} S={S} />
                <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 13 }}>
                  <button style={C.link} disabled={cards.indexOf(card) === 0} onClick={() => setI(Math.max(0, cards.indexOf(card) - 1))}>‹ Previous</button>
                  <button style={C.link} disabled={cards.indexOf(card) >= cards.length - 1} onClick={() => setI(cards.indexOf(card) + 1)}>Next ›</button>
                  <button style={C.link} onClick={load}>Check everything again</button>
                </div>
              </div>
              <div style={{ flex: '0 1 340px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={C.h}>Coming up</div>
                {cards.filter(c => c !== card).slice(0, 6).map(c => (
                  <button key={c.key} onClick={() => setI(cards.indexOf(c))}
                    style={{ ...C.card, padding: '11px 14px', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--aj-text)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 13.5 }}>{cardTitle(c)}</span>
                      {cardAmount(c) != null && <span style={{ ...C.mono, fontSize: 13 }}>{money(cardAmount(c))}</span>}
                    </div>
                  </button>
                ))}
                {cards.length > 7 && <div style={{ fontSize: 13, ...C.muted }}>+ {cards.length - 7} more</div>}
              </div>
            </div>
          ) : (
            <div style={{ ...C.card, padding: '28px 32px' }}>
              <div style={{ fontSize: 22, fontWeight: 600 }}>Nothing needs you.</div>
              <div style={{ fontSize: 15, ...C.muted, marginTop: 6 }}>Every transaction knows where it goes. Next: post anything not yet in Core, type the bank statement balances, check it ties, and close the month.</div>
              <div style={{ display: 'flex', gap: 14, marginTop: 18 }}>
                <button style={C.big} onClick={onNext}>Next: post, balances, close</button>
                {gone.size > 0 && <button style={C.quiet} onClick={load}>Check everything again first</button>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function cardTitle(c) {
  if (c.kind === 'account') return c.vendor
  if (c.kind === 'reimb') return 'Payment to ' + c.person
  if (c.kind === 'expense') return 'A company-paid expense?'
  if (c.kind === 'project') return c.row.vendor || 'A project cost'
  if (c.kind === 'print') return (c.orders[0] && c.orders[0].shop) || c.vendor
  if (c.kind === 'payee') return c.vendor + ' (not in Core)'
  if (c.kind === 'moneyin') return 'Money in, ' + shortDate(c.date)
  if (c.kind === 'stripe') return 'Stripe sweep, ' + shortDate(c.date)
  if (c.kind === 'held') return c.vendor || 'Held'
  if (c.kind === 'phaselist') return (c.phaseCodes || []).join(', ') + ' may refuse'
  return 'Something to check'
}
function cardAmount(c) {
  if (c.kind === 'account') return c.total
  if (c.kind === 'project') return Math.abs(Number(c.row.amount))
  return c.amount ?? null
}

/**
 * A print shop's charge matched to the print orders sent to it (Matt, 2026-11-02). One order: the
 * whole charge goes to it. Several: type each one's amount off the shop's statement; the boxes must
 * add up to the charge. Each line posts as billable reprographics on its project's phase.
 */
function PrintCard({ c, head, items, chart, busy, act, codeRow }) {
  const repro = (items || []).find(i => /reprographic/i.test(i.name)) || null
  const one = c.orders.length === 1
  const [lines, setLines] = useState(() => c.orders.map(o => ({
    on: one, orderId: o.id, phaseId: o.phases.length === 1 ? o.phases[0].id : '', amount: one ? c.amount.toFixed(2) : '',
    memo: ('Print order ' + shortDate(o.sentAt) + (o.summary ? ': ' + o.summary : '')).slice(0, 250), billable: true,
  })))
  const [other, setOther] = useState(false)
  const set = (k, patch) => setLines(ls => ls.map((l, i) => i === k ? { ...l, ...patch } : l))
  const chosen = lines.filter(l => l.on)
  const sum = Math.round(chosen.reduce((t, l) => t + (Number(String(l.amount).replace(/[$,]/g, '')) || 0), 0) * 100) / 100
  const left = Math.round((c.amount - sum) * 100) / 100
  const ready = repro && chosen.length > 0 && left === 0 && chosen.every(l => l.phaseId && Number(String(l.amount).replace(/[$,]/g, '')) > 0)
  const why = !repro ? 'Core has no Reprographics expense item.' : !chosen.length ? 'Tick the orders this charge covers.'
    : chosen.some(l => !l.phaseId) ? 'Pick the phase for each ticked order.' : left !== 0 ? `The amounts are ${money(Math.abs(left))} ${left > 0 ? 'short of' : 'over'} the charge.` : ''
  const save = () => act(() => setProjectSplit(c.rowId, chosen.map(l => {
    const o = c.orders.find(x => x.id === l.orderId), ph = o.phases.find(p => p.id === l.phaseId)
    return { projectId: l.phaseId, projectName: ph ? ph.code + ' - ' + ph.name.replace(/^\S+\s+-\s+/, '') : '', itemId: repro.id, itemName: repro.name,
      amount: Number(String(l.amount).replace(/[$,]/g, '')), billable: l.billable, memo: l.memo, printOrderId: l.orderId }
  })))
  const inp = { ...C.mono, fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }
  return (
    <>
      {head((c.orders[0] && c.orders[0].shop) || c.vendor, c.amount, [
        `${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`,
        one ? `One print order was sent to this shop before the charge, so the whole charge goes to it as billable reprographics.`
          : `${c.orders.length} print orders were sent to this shop before the charge. Tick the ones this charge covers and type each one's amount from the shop's statement.`,
      ])}
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {c.orders.map((o, k) => {
          const l = lines[k]
          return (
            <div key={o.id} style={{ border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '10px 12px', opacity: l.on ? 1 : 0.6 }}>
              <label style={{ display: 'flex', gap: 10, alignItems: 'baseline', cursor: 'pointer' }}>
                {!one && <input type="checkbox" checked={l.on} onChange={e => set(k, { on: e.target.checked })} />}
                <span style={{ flex: 1 }}><b>{o.projectNumber} {o.projectName}</b> <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>· sent {shortDate(o.sentAt)}</span>
                  {o.summary && <div style={{ fontSize: 13, ...C.muted, marginTop: 2 }}>{o.summary}</div>}</span>
              </label>
              {l.on && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, marginLeft: one ? 0 : 24 }}>
                  <select value={l.phaseId} onChange={e => set(k, { phaseId: e.target.value })} style={{ ...inp, fontFamily: '"DM Sans", sans-serif', maxWidth: 320 }}>
                    <option value="">Phase…</option>
                    {o.phases.map(p => <option key={p.id} value={p.id}>{p.code} · {String(p.name).replace(/^\S+\s+-\s+[^-]+-\s+/, '')}</option>)}
                  </select>
                  <input value={l.amount} disabled={one} inputMode="decimal" placeholder="Amount" onChange={e => set(k, { amount: e.target.value })} style={{ ...inp, width: 100, textAlign: 'right' }} />
                  <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={l.billable} onChange={e => set(k, { billable: e.target.checked })} /> Billable</label>
                  <input value={l.memo} onChange={e => set(k, { memo: e.target.value })} placeholder="What the client reads on the invoice" style={{ ...inp, fontFamily: '"DM Sans", sans-serif', flex: '1 1 260px' }} />
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14 }}>
        <button style={{ ...C.big, opacity: ready && !busy ? 1 : 0.4 }} disabled={!ready || busy} onClick={save}>
          {one ? 'Bill it to the project' : `Split ${money(c.amount)} across ${chosen.length || 'the'} order${chosen.length === 1 ? '' : 's'}`}
        </button>
        <span style={{ fontSize: 13.5, color: why ? 'var(--aj-text-mid)' : C.good }}>{why || 'Adds up to the charge.'}</span>
      </div>
      <div style={{ fontSize: 13, ...C.muted, marginTop: 16 }}>None of these orders? <button style={{ ...C.link, fontSize: 13 }} onClick={() => setOther(v => !v)}>Code it another way</button></div>
      {other && <AccountPicker chart={chart} busy={busy} context={{ description: c.description, amount: c.amount }} onPick={(code) => codeRow(c.rowId)(code)} />}
    </>
  )
}

/** Add the payee to Core as a vendor, by a name the person confirms (2026-11-02). */
function AddVendor({ vendor, onAdded }) {
  const title = String(vendor || '').replace(/\b[a-z]/g, ch => ch.toUpperCase()).replace(/\s+\d[\d\s]*$/, '').trim()
  const [name, setName] = useState(title)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const add = async () => {
    if (!(await confirmDialog({ title: 'Add ' + name.trim() + ' to Core?', confirmLabel: 'Add the vendor',
      message: `Core gets a new vendor named "${name.trim()}", on Net 30 like your other vendors. This payment is written to it when you post, and later payments the bank calls "${vendor}" go to it too.` }))) return
    setBusy(true); setErr('')
    try { await addCoreVendor(name, vendor); onAdded() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <label style={{ fontSize: 13, ...C.muted, width: '100%' }}>The name Core will use. Change it to the vendor's real business name if the bank shortens it.</label>
      <input value={name} onChange={e => setName(e.target.value)} style={{ fontSize: 14, padding: '9px 10px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', minWidth: 240, fontFamily: '"DM Sans", sans-serif' }} />
      <button style={C.big} disabled={busy || name.trim().length < 2} onClick={add}>{busy ? 'Adding…' : 'Add as a vendor in Core'}</button>
      {err && <div style={{ color: C.bad, fontSize: 13, width: '100%' }}>{err}</div>}
    </div>
  )
}

function Tile({ primary, label, sub, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} className={'aj-bc-tile' + (primary ? ' aj-bc-primary' : '')}
      style={{ minHeight: 96, padding: 16 }}>
      <span><span className="aj-bc-lab">{label}</span>{sub && <span className="aj-bc-sub">{sub}</span>}</span>
    </button>
  )
}

function AccountPicker({ chart, onPick, busy, context }) {
  const [q, setQ] = useState('')
  const [asking, setAsking] = useState(false)
  const needle = q.trim().toLowerCase()
  const hits = (chart || []).filter(a => !needle || a.name.toLowerCase().includes(needle) || a.code.startsWith(needle)).slice(0, 12)
  return (
    <div style={{ marginTop: 12, border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Type to find an account by name"
          style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', fontSize: 14, padding: '9px 11px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif' }} />
        <button style={{ ...C.link, whiteSpace: 'nowrap' }} onClick={() => setAsking(v => !v)}>{asking ? 'Back to the list' : 'Not sure? Describe it'}</button>
      </div>
      {asking
        ? <AskAccount context={context} busy={busy} onPick={onPick} />
        : (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
            {hits.map(a => (
              <button key={a.code} disabled={busy} onClick={() => onPick(a.code, a.name)}
                style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left', background: 'none', border: 'none', padding: '7px 6px', cursor: 'pointer', font: 'inherit', fontSize: 14, color: 'var(--aj-text)', borderBottom: '0.5px solid var(--aj-border)' }}>
                <span>{a.name}</span><span style={{ ...C.mono, fontSize: 12, ...C.muted }}>{a.code}</span>
              </button>
            ))}
            {!hits.length && <div style={{ fontSize: 13, ...C.muted, padding: 6 }}>No account by that name. Not sure? Describe it.</div>}
          </div>
        )}
    </div>
  )
}

/**
 * Describe it in your own words; the firm's own past payments answer first, and only when none match
 * does the AI suggest one (Matt, 2026-10-29). Every answer is a choice; nothing is picked for you.
 */
function AskAccount({ context, onPick, busy }) {
  const [text, setText] = useState('')
  const [res, setRes] = useState(null)
  const [working, setWorking] = useState(false)
  const [err, setErr] = useState('')
  const ask = async () => {
    if (text.trim().length < 3 || working) return
    setWorking(true); setErr(''); setRes(null)
    try { setRes(await askAccount(text, context || {})) } catch (e) { setErr(e.message) } finally { setWorking(false) }
  }
  const choice = (key, label, sub, code, name, primary) => (
    <button key={key} disabled={busy} onClick={() => onPick(code, name)} className={'aj-bc-tile' + (primary ? ' aj-bc-primary' : '')} style={{ minHeight: 0 }}>
      <span><span className="aj-bc-lab">{label}</span>{sub && <span className="aj-bc-sub">{sub}</span>}</span>
    </button>
  )
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <input autoFocus value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask() }}
          placeholder="What was it? e.g. our Arizona business registration renewal"
          style={{ flex: 1, minWidth: 0, fontSize: 14, padding: '9px 11px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif' }} />
        <button style={C.quiet} disabled={working || text.trim().length < 3} onClick={ask}>{working ? 'Looking…' : 'Ask'}</button>
      </div>
      {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 8 }}>{err}</div>}
      {res && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {res.precedents.length > 0 && (
            <>
              <div style={{ ...C.h, fontSize: 11 }}>The firm has paid something like this before</div>
              <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {res.precedents.map((p, i) => choice('p' + p.code, p.name, `Like "${p.because}", which went here`, p.code, p.name, i === 0))}
              </div>
            </>
          )}
          {res.ai && (
            <>
              <div style={{ ...C.h, fontSize: 11 }}>Nothing like it before. Suggested by AI</div>
              <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {choice('ai', res.ai.name, res.ai.why, res.ai.code, res.ai.name, true)}
              </div>
            </>
          )}
          {res.aiTried && !res.ai && <div style={{ fontSize: 13, ...C.muted }}>Nothing like it before, and no suggestion came back. Pick from the list.</div>}
          {res.byName.length > 0 && (
            <>
              <div style={{ ...C.h, fontSize: 11 }}>Accounts with those words in the name</div>
              <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {res.byName.map(a => choice('n' + a.code, a.name, null, a.code, a.name, false))}
              </div>
            </>
          )}
          <div style={{ fontSize: 12.5, ...C.muted }}>You decide. Nothing is chosen until you pick one.</div>
        </div>
      )}
    </div>
  )
}

function DecisionCard({ card: c, n, of, chart, items, onDone, onLookup, onRecheck, ProjectCoding, ConnectPayee, S }) {
  const [busy, setBusy] = useState(false)
  const [picking, setPicking] = useState(false)
  const [err, setErr] = useState('')
  // Whether an account answer becomes a rule for the vendor (Matt, 2026-11-02, on a one-off Zelle to
  // Casa Fox: "maybe we should be warned about that and allowed to indicate we don't want that rule").
  const [remember, setRemember] = useState(true)
  const [projecting, setProjecting] = useState(false)
  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); onDone() } catch (e) { setErr(e.message) } finally { setBusy(false) } }
  const nameOf = (code) => (chart.find(a => a.code === String(code)) || {}).name || code

  const applyVendor = (code, name) => act(async () => {
    for (const b of c.batchIds) await applyToVendor(b, c.vendor, code, name, remember)
  })
  const codeRow = (rowId) => (code) => act(() => updateRow(rowId, { accountCode: code, status: 'accepted' }))

  const head = (title, amount, lines) => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...C.mono, fontSize: 12.5, ...C.muted }}>
        <span>{n} OF {of}{c.from ? ' · ' + c.from.toUpperCase() : ''}</span>{c.late && <span>LATE ARRIVAL FOR {String(c.date || '').slice(0, 7)}</span>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, marginTop: 10 }}>
        <span style={{ fontSize: 26, fontWeight: 600, overflowWrap: 'anywhere' }}>{title}</span>
        {amount != null && <span style={{ ...C.mono, fontSize: 22, whiteSpace: 'nowrap' }}>{money(amount)}</span>}
      </div>
      {lines.filter(Boolean).map((l, k) => <div key={k} style={{ fontSize: k === 0 ? 12.5 : 15, ...(k === 0 ? { ...C.mono, ...C.muted } : {}), marginTop: k === 0 ? 6 : 12, lineHeight: 1.5 }}>{l}</div>)}
    </>
  )

  let body = null
  if (c.kind === 'account') {
    const s = c.suggestion
    body = (
      <>
        {head(c.vendor, c.total, [
          `${String(c.sample || '').slice(0, 90)} · ${c.count} charge${c.count === 1 ? '' : 's'}, ${shortDate(c.first)}${c.last !== c.first ? ' to ' + shortDate(c.last) : ''}`,
          c.bucket === 'historical_split'
            ? 'The books have coded this vendor several different ways before, so there is no single answer to offer.'
            : s ? <>The books haven't seen this vendor. The closest one they know, <b>{s.because}</b>, goes to <b>{nameOf(s.code)}</b>.</>
              : "The books haven't seen this vendor before.",
          c.inCore === false ? <span style={{ color: '#b8540a' }}>Core has no vendor called {c.vendor} yet. After you choose an account, the next card adds them to Core or connects them to one Core has.</span> : null,
        ])}
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, padding: '10px 12px', borderRadius: 6, background: 'var(--aj-bg-subtle)', fontSize: 14, cursor: 'pointer', lineHeight: 1.45 }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            <b>Use this for every {c.vendor} payment from now on.</b>{' '}
            <span style={C.muted}>{remember
              ? `Later payments to ${c.vendor} will be coded the same way without asking. Untick it for a one-off.`
              : `Only ${c.count === 1 ? 'this charge is' : 'these ' + c.count + ' charges are'} coded. The next ${c.vendor} payment will ask again.`}</span>
          </span>
        </label>
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', marginTop: 16 }}>
          {s && s.code !== 'SPLIT' && <Tile primary label={nameOf(s.code)} sub={`Same as ${s.because}`} disabled={busy} onClick={() => applyVendor(s.code, nameOf(s.code))} />}
          <Tile primary={!s} label="Pick an account" sub="Find it by name" disabled={busy} onClick={() => setPicking(v => !v)} />
          <Tile label="Owner distribution" sub="Split 50/50 between the owners" disabled={busy} onClick={() => applyVendor('3201/3202', 'Owner distribution, split 50/50')} />
          {c.count === 1 && <Tile label="It's a project cost" sub="Pick the project and expense item" disabled={busy} onClick={() => setProjecting(v => !v)} />}
        </div>
        {picking && <AccountPicker chart={chart} busy={busy} context={{ vendor: c.vendor, description: c.sample, amount: c.total }} onPick={(code, name) => applyVendor(code, name)} />}
        {projecting && c.count === 1 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, ...C.muted, marginBottom: 8 }}>Coded to a project, it is billed through Core and no rule is made for {c.vendor}.</div>
            <ProjectCoding row={{ id: c.rowIds[0], amount: -c.total, description: c.sample, billable: true, invoice_memo: '' }} items={items} onSaved={onDone} />
          </div>
        )}
      </>
    )
  } else if (c.kind === 'reimb') {
    body = (
      <>
        {head('Payment to ' + c.person, c.amount, [
          `${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`,
          <>This equals {c.bills.length} of {c.person}'s open expense bills exactly: {c.bills.map(b => b.number).join(', ')}. Recorded as paying them back, those bills close in Core.</>,
        ])}
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', marginTop: 16 }}>
          <Tile primary label="Pays them back" sub="Close those expense bills" disabled={busy} onClick={() => act(() => linkReimbToRow(c.rowId, c.bills.map(b => b.id)))} />
          <Tile label="Something else" sub="Pay, or another account" disabled={busy} onClick={() => setPicking(v => !v)} />
        </div>
        {picking && <AccountPicker chart={chart} busy={busy} context={{ description: c.description, amount: c.amount }} onPick={(code) => codeRow(c.rowId)(code)} />}
      </>
    )
  } else if (c.kind === 'expense') {
    body = (
      <>
        {head(String(c.description || '').slice(0, 40), c.amount, [
          `${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`,
          c.candidates.length === 1 ? 'This looks like an approved company-paid expense:' : `${c.candidates.length} approved company-paid expenses match this charge:`,
        ])}
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', marginTop: 16 }}>
          {c.candidates.map((e, k) => (
            <Tile key={e.id} primary={k === 0} label={[e.filer, String(e.item || '').replace(/^EX-\s*/, '')].filter(Boolean).join(' · ') || 'This expense'}
              sub={[e.project, e.date && shortDate(e.date), e.memo || e.payee].filter(Boolean).join(' · ')} disabled={busy}
              onClick={() => act(() => linkExpenseToRow(c.rowId, e.id))} />
          ))}
          <Tile label="None of these" sub="Pick an account instead" disabled={busy} onClick={() => setPicking(v => !v)} />
        </div>
        {picking && <AccountPicker chart={chart} busy={busy} context={{ description: c.description, amount: c.amount }} onPick={(code) => codeRow(c.rowId)(code)} />}
      </>
    )
  } else if (c.kind === 'phaselist') {
    body = (
      <>
        {head(c.vendor, c.amount, [
          `${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`,
          <>Core may not accept this payment: {c.problems.join('; ')}. If it doesn't, posting stops there.</>,
          <>To be sure, open {(c.phaseCodes || []).join(' and ')} in Core › Assignments and remove the vendor and expense item lists (an empty list allows everything; leave team members alone), then check again. If you know the vendor is allowed, post it as is.</>,
        ])}
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', marginTop: 16 }}>
          <Tile primary label="I cleared it, check again" sub="Read the phase's lists in Core again" disabled={busy} onClick={onRecheck} />
          <Tile label="Post it as is" sub="The vendor is on the list; try it" disabled={busy} onClick={() => act(() => phaseListOk(c.rowId))} />
        </div>
      </>
    )
  } else if (c.kind === 'print') {
    body = <PrintCard c={c} head={head} items={items} chart={chart} busy={busy} act={act} codeRow={codeRow} />
  } else if (c.kind === 'project') {
    body = (
      <>
        {head(c.row.vendor || 'A project cost', Math.abs(Number(c.row.amount)), [
          `${String(c.row.description || '').slice(0, 90)} · ${shortDate(c.row.posted_on)}`,
          'This is a project cost. Which project, and what kind of expense? The kind decides the account.',
        ])}
        <div style={{ marginTop: 14 }}><ProjectCoding row={c.row} items={items} onSaved={onDone} /></div>
        {/* The guess that it's a project cost can be wrong (a personal car repair once matched "City of
            Irvine" on the one word "Irvine"), so the card always offers the way out (Matt, 2026-10-29). */}
        <div style={{ fontSize: 13, ...C.muted, marginTop: 14 }}>Not a project cost?</div>
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', marginTop: 8 }}>
          <Tile label="Owner distribution" sub="Split 50/50 between the owners" disabled={busy} onClick={() => codeRow(c.row.id)('3201/3202')} />
          <Tile label="Pick an account" sub="Find it by name, or describe it" disabled={busy} onClick={() => setPicking(v => !v)} />
        </div>
        {picking && <AccountPicker chart={chart} busy={busy} context={{ description: c.row.description, amount: c.row.amount }} onPick={(code) => codeRow(c.row.id)(code)} />}
      </>
    )
  } else if (c.kind === 'payee') {
    body = (
      <>
        {head(c.vendor, c.amount, [
          `${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`,
          "Core has no vendor for this payment, and a payment can't be entered without one. Add them as a vendor here (check the name first), or connect them to one Core already has.",
        ])}
        <AddVendor vendor={c.vendor} onAdded={onRecheck} />
        <div style={{ fontSize: 13, ...C.muted, marginTop: 16 }}>Or, if Core has them under another name:</div>
        <div style={{ marginTop: 8 }}><ConnectPayee S={S} pattern={c.vendor} onLinked={onDone} /></div>
        <button style={{ ...C.link, fontSize: 13, marginTop: 10 }} onClick={onRecheck}>I added them in Core myself, check again</button>
      </>
    )
  } else if (c.kind === 'moneyin') {
    // No client payment in Core matches this. What else it can be, decided here and written at Post
    // (Matt, 2026-10-29: "How come I can't make a deposit from here if there isn't a match?").
    const otherName = c.otherBank === '1120' ? 'savings' : c.otherBank === '1110' ? 'checking' : null
    const book = (code, label) => act(() => setMoneyIn(c.rowId, { kind: 'lines', code, label }))
    // Bank interest is income, not a refund (Matt, 2026-10-30): its own choice, first when the bank says so.
    const interestAcct = (chart || []).find(a => /interest/i.test(a.name || '') && /income|earned|revenue/i.test(a.name || ''))
      || (chart || []).find(a => /interest/i.test(a.name || '') && /^[4-9]/.test(String(a.code)))
    const saysInterest = /INTEREST/i.test(c.description || '')
    body = (
      <>
        {head('Money in, ' + shortDate(c.date), c.amount, [
          String(c.description || '').slice(0, 100),
          'No client payment in Core matches this. What is it?',
        ])}
        <div className="aj-bc-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', marginTop: 16 }}>
          {c.refundOf && !saysInterest && <Tile primary label={'Refund from ' + String(c.refundOf.vendor).split(' ').slice(0, 2).map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
            sub={`Back to ${c.refundOf.name || c.refundOf.code}, where its charges go`} disabled={busy}
            onClick={() => book(String(c.refundOf.code), 'Refund from ' + c.refundOf.vendor)} />}
          {interestAcct && saysInterest && <Tile primary label="Interest earned" sub={'Bank interest, booked to ' + interestAcct.name} disabled={busy}
            onClick={() => book(String(interestAcct.code), 'Interest earned')} />}
          {(c.retainers || []).map(i => (
            <Tile key={i.id} primary label={'Retainer, ' + (String(i.project || '').replace(/^[0-9-]+\s*-\s*/, '') || i.client)}
              sub={`Settles retainer invoice ${i.number} to ${i.client}, sent ${shortDate(i.date)}`} disabled={busy}
              onClick={() => act(() => setMoneyIn(c.rowId, { kind: 'retainer', invoiceId: i.id }))} />
          ))}
          {otherName && <Tile label={'Transfer from ' + otherName} sub="Moving the firm's own money between its accounts" disabled={busy}
            onClick={() => act(() => setMoneyIn(c.rowId, { kind: 'transfer' }))} />}
          <Tile label="Distribution returned" sub="Money back on an owner distribution, split 50/50" disabled={busy}
            onClick={() => book('3201/3202', 'Owner distribution returned')} />
          <Tile label="Owners put money in" sub="An owner contribution, split 50/50" disabled={busy}
            onClick={() => book('3101/3102', 'Owner contribution')} />
          {interestAcct && !saysInterest && <Tile label="Interest earned" sub={'Bank interest, booked to ' + interestAcct.name} disabled={busy}
            onClick={() => book(String(interestAcct.code), 'Interest earned')} />}
          <Tile label="Something else" sub="A refund, a rebate, anything else: pick the account it belongs to" disabled={busy} onClick={() => setPicking(v => !v)} />
        </div>
        {picking && <AccountPicker chart={chart} busy={busy} context={{ description: c.description, amount: c.amount }}
          onPick={(code, name) => book(String(code), (name || code))} />}
        <div style={{ fontSize: 13, ...C.muted, marginTop: 16 }}>
          A client payment? Record it against the invoice in Core, then <button style={C.link} onClick={onRecheck}>check again</button>. Or <button style={C.link} onClick={onLookup}>look up and fix</button>.
        </div>
      </>
    )
  } else if (c.kind === 'stripe') {
    // A Stripe sweep that could not post by itself: which of its payments Core is missing.
    body = (
      <>
        {head('Stripe sweep, ' + shortDate(c.date), c.amount, [
          String(c.description || '').slice(0, 100),
          c.reason || `${(c.items || []).length} client payment${(c.items || []).length === 1 ? '' : 's'}, ${money(c.gross || 0)} paid, less ${money(c.fees || 0)} Stripe fees. It goes into Core as one deposit once every payment is waiting in Undeposited Funds.`,
        ])}
        {(c.items || []).length > 0 && (
          <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.7 }}>
            {c.items.map((it, k) => (
              <div key={k} style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                <span>{it.ok ? '✓' : '•'} {it.invoiceNumber ? 'Invoice ' + it.invoiceNumber : 'Payment'} <span style={{ ...C.muted }}>{it.ok ? 'in Core, waiting to be deposited' : it.problem}</span></span>
                <span style={{ ...C.mono }}>{money(it.gross)}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 13.5, ...C.muted, marginTop: 14, lineHeight: 1.5 }}>
          For each payment marked •, Core needs it recorded against its invoice with Deposit To set to <b>1211 Undeposited Funds</b>. If it was recorded into checking instead, open that payment in Core and change Deposit To to 1211. Then check again.
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
          <button style={C.quiet} onClick={onRecheck}>Fixed in Core, check again</button>
          <button style={C.link} onClick={onLookup}>Look up and fix</button>
        </div>
      </>
    )
  } else if (c.kind === 'held') {
    body = (
      <>
        {head(c.vendor || 'Held', c.amount, [`${String(c.description || '').slice(0, 90)} · ${shortDate(c.date)}`, c.reason])}
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button style={C.quiet} onClick={onRecheck}>Fixed, check again</button>
          <button style={C.link} onClick={onLookup}>Look up and fix</button>
        </div>
      </>
    )
  } else {
    body = (
      <>
        {head('Something could not be checked', null, ['', c.say])}
        <button style={{ ...C.quiet, marginTop: 16 }} onClick={onRecheck}>Check again</button>
      </>
    )
  }
  return (
    <div style={{ ...C.card, padding: '26px 30px', opacity: busy ? 0.6 : 1 }}>
      {body}
      {err && <div style={{ color: C.bad, fontSize: 13.5, marginTop: 12 }}>{err}</div>}
    </div>
  )
}

// ── post, tie out, close ─────────────────────────────────────────────────────

function Post({ month, onBack, onClosed }) {
  const [plan, setPlan] = useState(null)
  const [tie, setTie] = useState(null)
  const [err, setErr] = useState('')
  const [progress, setProgress] = useState('')
  const [bar, setBar] = useState(null)   // { n, total } while posting: the progress bar (Matt, 2026-11-02)
  const [stopped, setStopped] = useState(null)
  const [why, setWhy] = useState('')
  const [statements, setStatements] = useState(null)
  const stOk = !!statements && statements.accounts.every(x => x.complete)
  // Only a balance that's been TYPED and disagrees asks for a note; blank ones are just still to do
  // (it used to say "does not match" before anything was typed).
  const mismatch = !!statements && statements.accounts.some(x => x.complete && !x.matches)
  const needWhy = !!tie && (!tie.ties || mismatch)
  const [cpaOwed, setCpaOwed] = useState(null)   // reports owed to the CPA for periods ended by this month (null = not read yet)
  const closeReady = !!tie && stOk && cpaOwed === 0 && (!needWhy || why.trim().length >= 10)
  const [closing, setClosing] = useState(false)
  const [skipSetAside, setSkipSetAside] = useState([])   // bucket keys not to set aside this month
  // The first close the tool ever does posts a SAMPLE first (Matt, 2026-10-29: "a big test as it's the
  // first time"): a few payments, one deposit, one transfer, then it stops so they can be read back in
  // Core before the rest go in. After one real close, Post just posts.
  const [firstClose, setFirstClose] = useState(false)
  const [sampled, setSampled] = useState(false)
  useEffect(() => { getCloseStatus().then(s => setFirstClose(!(s.history || []).some(h => !h.seeded))).catch(() => setFirstClose(true)) }, [])

  const load = useCallback(async () => {
    setErr(''); setPlan(null); setTie(null); setStopped(null)
    try {
      const p = await getClosePlan()
      setPlan(p)
      const nothing = !p.post.checks.length && !p.post.deposits.length && !(p.post.depositLines || []).length && !(p.post.retainers || []).length && !(p.post.stripeSweeps || []).length && !p.post.transfers.length
      if (nothing && !p.cards.length) setTie(await getCloseTie())
    } catch (e) { setErr(e.message) }
  }, [])
  useEffect(() => { load() }, [load])

  async function postAll(sample) {
    const full = plan.post
    const p = sample
      ? { checks: full.checks.slice(0, 5), deposits: full.deposits.slice(0, 1), depositLines: (full.depositLines || []).slice(0, 1), retainers: (full.retainers || []).slice(0, 1), stripeSweeps: (full.stripeSweeps || []).slice(0, 1), transfers: full.transfers.slice(0, 1) }
      : { ...full, depositLines: full.depositLines || [], retainers: full.retainers || [], stripeSweeps: full.stripeSweeps || [] }
    const total = p.checks.length + p.deposits.length + p.depositLines.length + p.retainers.length + p.stripeSweeps.length + p.transfers.length
    if (!(await confirmDialog({ title: sample ? `Post a sample of ${month.name}?` : `Post ${month.name}?`, danger: true,
      confirmLabel: sample ? `Post ${total}` : `Post ${monthOnly(month.name)}`,
      message: sample
        ? `${total} entries go into BQE Core, then it stops so they can be checked in Core before the rest. Core has no undo.`
        : `${total} entries go into BQE Core. Core has no undo. Nothing already in Core is touched.` }))) return
    let n = 0
    try {
      setBar({ n: 0, total })
      for (let k = 0; k < p.checks.length; k += 10) {
        setProgress(`Posting ${Math.min(n + 10, total)} of ${total}…`)
        const r = await commitRows(p.checks.slice(k, k + 10))
        const bad = (r.results || []).find(x => !x.ok)
        n += (r.results || []).filter(x => x.ok).length
        setBar({ n, total, last: ((r.results || []).filter(x => x.ok).slice(-1)[0] || {}).basis || '' })
        if (bad) { setStopped({ done: n, total, why: bad.refused, detail: bad.detail || '' }); setProgress(''); return }
      }
      for (let k = 0; k < p.deposits.length; k += 25) {
        setProgress(`Posting ${Math.min(n + 25, total)} of ${total}…`)
        const r = await commitDeposits(p.deposits.slice(k, k + 25))
        n += (r.created || []).length
        setBar({ n, total })
        if ((r.failed || []).length) { setStopped({ done: n, total, why: r.failed[0].error || 'Core refused a deposit.' }); setProgress(''); return }
      }
      for (const d of p.retainers) {
        setProgress(`Posting ${n + 1} of ${total}…`)
        const r = await writeRetainerPayment(d)
        n++
        setBar({ n, total })
        if (r && r.ledgerOk === false) { setStopped({ done: n, total, why: 'The retainer went into Core but did not read back as expected (' + (d.label || '') + '). Check it in Core before going on.' }); setProgress(''); return }
      }
      for (const d of p.stripeSweeps) {
        setProgress(`Posting ${n + 1} of ${total}…`)
        await writeDepositWithLines(d.rowId, d.lines, d.label, d.paymentIds)
        n++
        setBar({ n, total })
      }
      for (const d of p.depositLines) {
        setProgress(`Posting ${n + 1} of ${total}…`)
        await writeDepositWithLines(d.rowId, d.lines, d.label || undefined)
        n++
        setBar({ n, total })
      }
      for (const t of p.transfers) {
        setProgress(`Posting ${n + 1} of ${total}…`)
        await writeFundTransfer(t.rowId, t.mirrorRowId)
        n++
        setBar({ n, total })
      }
    } catch (e) { setStopped({ done: n, total, why: e.message }); setProgress(''); return }
    if (sample) {
      setProgress(''); setSampled(true)
      const fresh = await getClosePlan().catch(() => null)
      if (fresh) setPlan(fresh)
      return
    }
    setProgress('Checking it ties to the bank…')
    try { setTie(await getCloseTie()) } catch (e) { setErr(e.message) }
    setProgress('')
    const fresh = await getClosePlan().catch(() => null)
    if (fresh) setPlan(fresh)
  }

  async function close() {
    setClosing(true); setErr('')
    try { onClosed(await finishClose(needWhy ? why : '', skipSetAside)) } catch (e) { setErr(e.message) } finally { setClosing(false) }
  }

  const p = plan && plan.post
  const count = p ? p.checks.length + p.deposits.length + (p.depositLines || []).length + (p.retainers || []).length + (p.stripeSweeps || []).length + p.transfers.length : 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Header title={`Close ${month.name}`} step="post" onBack={onBack} backLabel="Decide" />
      {err && <div style={{ color: C.bad }}>{err}</div>}
      {!plan && !err && <div style={{ fontSize: 15, ...C.muted }}>Preparing what goes into Core…</div>}
      {plan && plan.cards.length > 0 && (
        <div style={{ ...C.card, borderColor: C.bad }}>
          {plan.cards.length} decision{plan.cards.length === 1 ? ' is' : 's are'} still open. <button style={C.link} onClick={onBack}>Back to Decide</button>
        </div>
      )}
      {plan && !plan.cards.length && tie && (
        <Finish month={month} tie={tie} onStatements={setStatements} stOk={stOk} why={why} setWhy={setWhy} skip={skipSetAside} setSkip={setSkipSetAside}
          needWhy={needWhy} mismatch={mismatch} closeReady={closeReady} closing={closing} onClose={close} onCpaOwed={setCpaOwed} cpaOwed={cpaOwed} />
      )}
      {plan && !plan.cards.length && !tie && (
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 520px', minWidth: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 600 }}>{count ? "Here's what goes into Core" : 'Everything is in Core'}</div>
            <div style={{ fontSize: 15, ...C.muted, marginTop: 6 }}>
              {count ? 'Nothing already in Core is touched.' : <>Every {month.name} transaction is already in Core. What's left is on the right: type each account's beginning and ending balance off the Chase statement, then press <b style={{ color: 'var(--aj-text)' }}>Close {monthOnly(month.name)}</b>.</>}
            </div>
            {count > 0 && (
              <div style={{ ...C.card, padding: '6px 22px', marginTop: 16 }}>
                {p.byAccount.map(a => <Line key={a.label} label={a.label} n={a.n} amount={a.total} items={a.items} />)}
                {p.transfers.length > 0 && <Line label="Transfers between your accounts" n={p.transfers.length} amount={p.transfersTotal} items={p.transfers.map(t => ({ id: t.rowId, date: t.date, who: t.direction, what: '', amount: t.amount }))} />}
                {p.deposits.length > 0 && <Line label="Client payments deposited" n={p.deposits.length} amount={p.depositsTotal} plus items={p.deposits.map(d => ({ id: d.id, date: d.date, who: '', what: d.what || '', amount: d.amount }))} />}
                {(p.stripeSweeps || []).map(d => <Line key={d.rowId} label={d.label} n={1} amount={d.amount} plus />)}
                {(p.retainers || []).map(d => <Line key={d.rowId} label={d.label} n={1} amount={d.amount} plus />)}
                {(p.depositLines || []).map(d => <Line key={d.rowId} label={'Deposited: ' + (d.label || 'to an account')} n={1} amount={d.amount} plus />)}
              </div>
            )}
          </div>
          <div style={{ flex: '0 1 380px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {count > 0 && !tie && !stopped && (
              <div style={{ ...C.card, border: '1.5px solid ' + C.bad }}>
                <div style={{ ...C.h, color: C.bad }}>Writes to the ledger</div>
                <div style={{ fontSize: 15, lineHeight: 1.5, margin: '10px 0 16px' }}>
                  {count} entr{count === 1 ? 'y' : 'ies'} into Core for {month.name}. Each is checked against the bank as it goes in; if Core refuses one, it stops there and says why.
                </div>
                {firstClose && !sampled ? (
                  <>
                    <div style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 12 }}>
                      This is the first month the close posts, so it starts with a sample: up to 5 payments, 1 deposit and 1 transfer. Check them in Core, then post the rest.
                    </div>
                    <button style={{ ...C.danger, width: '100%', opacity: progress ? 0.6 : 1 }} disabled={!!progress} onClick={() => postAll(true)}>
                      {progress || 'Post the sample'}
                    </button>
                    {progress && bar && <PostBar bar={bar} />}
                  </>
                ) : (
                  <>
                    {sampled && <div style={{ fontSize: 13.5, color: C.good, marginBottom: 12 }}>✓ Sample posted. Once it checks out in Core, post the rest.</div>}
                    <button style={{ ...C.danger, width: '100%', opacity: progress ? 0.6 : 1 }} disabled={!!progress} onClick={() => postAll(false)}>
                      {progress || (sampled ? 'Post the rest' : `Post ${monthOnly(month.name)}`)}
                    </button>
                    {progress && bar && <PostBar bar={bar} />}
                  </>
                )}
              </div>
            )}
            {/* One next step at a time (Matt, 2026-11-02: "Post again" here and "Post October" above
                read as two choices). While stopped, the posting button is hidden; checking again re-reads
                what is left and brings it back with the remaining count. */}
            {stopped && <StoppedCard stopped={stopped} onCheck={load} />}
            {progress && !count && <div style={C.muted}>{progress}</div>}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Finishing the month once everything is in Core (Matt, 2026-11-02: the old right-hand column of
 * cards was "very poorly organized visually"). One column, numbered steps in order: the month
 * matches the bank, type the statement balances, a note only if something disagrees, then Close.
 */
function Finish({ month, tie, onStatements, stOk, why, setWhy, skip, setSkip, needWhy, mismatch, closeReady, closing, onClose, onCpaOwed, cpaOwed }) {
  const mo = monthOnly(month.name)
  const stepH = { display: 'flex', gap: 12, alignItems: 'center', fontSize: 17, fontWeight: 600 }
  const num = (n, ok) => (
    <span style={{ width: 26, height: 26, flex: '0 0 26px', borderRadius: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 600, background: ok ? C.good : 'var(--aj-bg-subtle)', color: ok ? '#fff' : 'var(--aj-text)',
      border: ok ? 'none' : '0.5px solid var(--aj-border-mid)' }}>{ok ? '✓' : n}</span>
  )
  const reason = !stOk ? 'Type all the statement balances first.'
    : cpaOwed ? 'Add the CPA reports first.'
    : needWhy && why.trim().length < 10 ? "Say why it doesn't match first (a sentence)." : ''
  const colH = { ...C.muted, fontSize: 12.5, textAlign: 'right' }
  return (
    <div style={{ maxWidth: 1040, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 28, fontWeight: 600 }}>Finish {month.name}</div>
        <div style={{ fontSize: 15, ...C.muted, marginTop: 6 }}>Every {mo} transaction is in Core. Check the balances against the bank statements, then close.</div>
      </div>

      <div style={C.card}>
        <div style={stepH}>{num(1, tie.ties)}<span>{tie.ties ? `${mo}'s activity matches the bank` : `${mo}'s activity doesn't match the bank yet`}</span></div>
        <div style={{ marginTop: 12, marginLeft: 38, display: 'grid', gridTemplateColumns: 'minmax(140px,1fr) 150px 150px 110px', rowGap: 8, columnGap: 16, fontSize: 14, alignItems: 'baseline' }}>
          <span /><span style={colH}>Bank</span><span style={colH}>Core</span><span />
          {Object.values(tie.tie).map(t => [
            <span key={t.name + 'n'}>{t.name}</span>,
            <span key={t.name + 'b'} style={{ ...C.mono, textAlign: 'right' }}>{money(t.bank.net)}</span>,
            <span key={t.name + 'c'} style={{ ...C.mono, textAlign: 'right' }}>{money(t.core.net)}</span>,
            <span key={t.name + 's'} style={{ color: t.difference === 0 ? C.good : C.bad, fontSize: 13.5 }}>{t.difference === 0 ? ((t.timing || []).length ? '✓ same, after timing' : '✓ same') : 'off by ' + money(t.difference)}</span>,
          ])}
        </div>
        {Object.values(tie.tie).filter(t => (t.timing || []).length).map(t => (
          <div key={t.name + 'tm'} style={{ marginLeft: 38, marginTop: 6, fontSize: 12.5, ...C.muted, lineHeight: 1.5 }}>
            {t.name}: {t.timing.map((i, k) => <span key={k}>{k ? '; ' : ''}{String(i.kind).toLowerCase()} {money(Math.abs(i.amount))}{i.who ? ' (' + String(i.who).replace(/;.*$/, '').slice(0, 40) + ')' : ''}, Core {i.coreDate === 'earlier month' ? 'an earlier month' : shortDate(i.coreDate)}, bank {shortDate(i.bankDate)}{i.pending ? ' (still pending then)' : ''}</span>)}. Dated on different sides of the month end; nothing to fix.
          </div>
        ))}
        <div style={{ marginLeft: 38, marginTop: 8, fontSize: 12.5, ...C.muted }}>Money in less money out during {mo}, each account.</div>
        {!tie.ties && <div style={{ marginLeft: 38, marginTop: 10 }}><TieDetail tie={tie} /></div>}
      </div>

      <StatementsCard onChange={onStatements} step={num(2, stOk && !mismatch)} stepH={stepH} />

      <ClearedCard month={month} num={num} stepH={stepH} />

      <PaymentsCard month={month} num={num} stepH={stepH} />

      <ReimbursablesCard month={month} num={num} stepH={stepH} />

      <SetAsideCard month={month} num={num} stepH={stepH} skip={skip} setSkip={setSkip} />

      <CpaOwedStep month={month} num={num} stepH={stepH} onOwed={onCpaOwed} />

      {needWhy && (
        <div style={{ ...C.card, borderColor: C.bad }}>
          <div style={stepH}>{num(cpaOwed == null || cpaOwed === 0 ? 7 : 8, why.trim().length >= 10)}<span>Say why it doesn't match</span></div>
          <div style={{ marginLeft: 38 }}>
            <div style={{ fontSize: 13.5, ...C.muted, marginTop: 8, lineHeight: 1.5 }}>
              {!tie.ties ? tie.note : 'A balance you typed differs from the downloads and Core.'} If that's expected, say so and close; the note stays with the month.
            </div>
            <textarea value={why} onChange={e => setWhy(e.target.value)} rows={3} placeholder="Why it doesn't match"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, fontSize: 14, padding: 10, borderRadius: 6, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif' }} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 4 }}>
        <button style={{ ...C.big, opacity: closeReady && !closing ? 1 : 0.4 }} disabled={!closeReady || closing} onClick={onClose}>
          {closing ? 'Closing…' : `Close ${mo}`}
        </button>
        <span style={{ fontSize: 13.5, ...C.muted }}>{reason || `Books will be current through ${fullDate(month.end)}.`}</span>
      </div>
    </div>
  )
}

/**
 * Client payments (Matt, 2026-11-02: "How do I know if payments were applied correctly to invoices?").
 * The month's payments with what each paid and anything odd flagged, then the invoices still owed
 * after 60 days, each marked Still owed or Look into it (or written off) so the list gets reviewed once
 * and then only grows by the new ones.
 */
function PaymentsCard({ month, num, stepH }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [busy, setBusy] = useState('')
  const load = useCallback(() => { setErr(''); return getClosePayments().then(setD).catch(e => setErr(e.message)) }, [])
  useEffect(() => { load() }, [load])
  const mo = monthOnly(month.name)
  if (!d) return <div style={C.card}><div style={stepH}>{num(4, false)}<span>Check the client payments</span></div>
    <div style={{ marginLeft: 38, marginTop: 8, fontSize: 13.5, color: err ? C.bad : 'var(--aj-text-mid)' }}>{err || 'Reading payments and open invoices from Core…'}</div></div>
  const flagged = d.payments.filter(p => p.flags.length)
  const unmarked = d.old.filter(i => !i.review || i.review.mark === 'look')
  const allOk = !flagged.length && !unmarked.length
  const shown = showAll ? d.payments : flagged
  const mark = async (inv, m) => {
    let note = ''
    if (m === 'look') { const n = await promptDialog({ title: 'Not sure invoice ' + inv.number + ' is owed', message: 'What makes you unsure? It stays open here until it is marked owed or written off.', multiline: true }); if (n === null) return; note = n }
    setBusy(inv.id)
    try { await markInvoice(inv.id, m, note); await load() } catch (e) { setErr(e.message) } finally { setBusy('') }
  }
  const writeOff = async (inv) => {
    if (!(await confirmDialog({ title: 'Write off invoice ' + inv.number + '?', danger: true, confirmLabel: 'Write off ' + money(inv.owed),
      message: `${money(inv.owed)} owed by ${inv.client || 'the client'} on invoice ${inv.number} goes to Bad Debt (6042) in Core, and the invoice reads paid. Core has no undo.` }))) return
    setBusy(inv.id)
    try {
      const r = await writeOffInvoice(inv.id, inv.owed, true)
      if (!r.ledgerOk) await alertDialog({ title: 'Written, but check it in Core', message: `Core took the write-off but it did not read back as expected (invoice shows ${r.invoiceBalance == null ? 'unknown' : money(r.invoiceBalance)}).` })
      await load()
    } catch (e) { setErr(e.message) } finally { setBusy('') }
  }
  const grid = { display: 'grid', gridTemplateColumns: '58px minmax(150px,1fr) 120px minmax(200px,1.4fr) 110px', columnGap: 14, alignItems: 'baseline' }
  const right = { ...C.mono, textAlign: 'right' }
  return (
    <div style={C.card}>
      <div style={stepH}>{num(4, allOk)}<span>Check the client payments</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.55 }}>
          {d.payments.length} payment{d.payments.length === 1 ? '' : 's'} recorded in {mo}.{' '}
          {flagged.length ? <b style={{ color: C.bad }}>{flagged.length} with something to do, each saying what.</b>
            : <span style={{ color: C.good }}>✓ Each one is applied and its invoices read paid.</span>}{' '}
          <button style={{ ...C.link, fontSize: 13 }} onClick={() => setShowAll(v => !v)}>{showAll ? 'Show only the ones to look at' : 'Show all of them'}</button>
        </div>
        {shown.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 13.5 }}>
            <div style={{ ...grid, ...C.muted, fontSize: 12, paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border)' }}>
              <span>Date</span><span>Client</span><span>How</span><span>Invoices</span><span style={{ textAlign: 'right' }}>Amount</span>
            </div>
            {shown.map(p => (
              <div key={p.id} style={{ padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                <div style={grid}>
                  <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(p.date)}</span>
                  <span>{p.client}</span>
                  <span style={C.muted}>{p.how}</span>
                  <span>{p.invoices.length ? p.invoices.map(i => i.number + (i.owedNow > 0 ? ' (owes ' + money(i.owedNow) + ')' : ' ✓')).join(', ') : p.how === 'Retainer received' ? 'held as retainer' : '—'}</span>
                  <span style={right}>{money(p.amount)}</span>
                </div>
                {p.flags.map(f => <div key={f} style={{ ...grid, fontSize: 12.5, color: C.bad, marginTop: 3 }}><span /><span style={{ gridColumn: '2 / span 4' }}>{f}</span></div>)}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 20, paddingTop: 12, borderTop: '0.5px solid var(--aj-border)' }}>
          <div style={{ fontSize: 14, lineHeight: 1.55 }}>
            <b>Invoices still owed after 60 days</b> · {d.old.length} for {money(d.oldTotal)}: invoices Core shows money owed on, dated more than 60 days before {fullDate(month.end)}.{' '}
            {unmarked.length ? <span style={C.muted}>{unmarked.length} to settle: mark each one still owed, not sure it's owed, or write it off.</span>
              : <span style={{ color: C.good }}>✓ All reviewed.</span>}
          </div>
          {d.old.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 13.5 }}>
              {d.old.map(i => (
                <div key={i.id} style={{ display: 'grid', gridTemplateColumns: '78px minmax(200px,1fr) 100px 100px 250px', columnGap: 14, alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--aj-border)', opacity: busy === i.id ? 0.5 : 1 }}>
                  <span style={{ ...C.mono, fontSize: 12.5 }}>{i.number}</span>
                  <span>
                    {i.client || '—'}{i.lateFee && <span style={{ ...C.muted }}> · late fee</span>}
                    <div style={{ fontSize: 12, ...C.muted, lineHeight: 1.35 }}>{i.project}</div>
                    {i.review && i.review.note && <div style={{ fontSize: 12, color: '#b8540a' }}>{i.review.note}</div>}
                  </span>
                  <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(i.date)} · {i.daysOld}d</span>
                  <span style={right}>{money(i.owed)}</span>
                  <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {i.review ? (
                      <>
                        <span style={{ fontSize: 13, color: i.review.mark === 'owed' ? C.good : '#b8540a' }}>{i.review.mark === 'owed' ? '✓ Still owed' : "Not sure it's owed"}</span>
                        {i.review.mark === 'look' && <button style={{ ...C.quiet, padding: '5px 9px', fontSize: 12.5 }} disabled={!!busy} onClick={() => mark(i, 'owed')}>It's owed</button>}
                        {i.review.mark === 'look' && i.oneLine && <button style={{ ...C.quiet, padding: '5px 9px', fontSize: 12.5, color: C.bad }} disabled={!!busy} onClick={() => writeOff(i)}>Write off</button>}
                      </>
                    ) : (
                      <>
                        <button style={{ ...C.quiet, padding: '5px 9px', fontSize: 12.5 }} disabled={!!busy} onClick={() => mark(i, 'owed')}>Still owed</button>
                        <button style={{ ...C.quiet, padding: '5px 9px', fontSize: 12.5 }} disabled={!!busy} onClick={() => mark(i, 'look')}>Not sure it's owed</button>
                        {i.oneLine && <button style={{ ...C.quiet, padding: '5px 9px', fontSize: 12.5, color: C.bad }} disabled={!!busy} onClick={() => writeOff(i)}>Write off</button>}
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  )
}

/**
 * What closing sets aside (Matt, 2026-11-02): each bucket's amount for the month and how it is
 * figured, then the buckets' running total beside the savings account's real balance.
 */
function SetAsideCard({ month, num, stepH, skip, setSkip }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => { getSetAsidePreview().then(setD).catch(e => setErr(e.message)) }, [])
  const mo = monthOnly(month.name)
  if (!d) return <div style={C.card}><div style={stepH}>{num(6, false)}<span>What closing {mo} sets aside</span></div>
    <div style={{ marginLeft: 38, marginTop: 8, fontSize: 13.5, color: err ? C.bad : 'var(--aj-text-mid)' }}>{err || 'Figuring the month…'}</div></div>
  const s = d.setAside, b = s.basis, t = s.targets || {}
  const lines = [
    ['tax', "Owners' taxes", s.tax, `${t.taxPctOfProfit || 0}% of ${money(b.profit)} profit: ${money(s.taxEach)} for each of you, held for the estimated tax payments`],
    ['planet', '1% for the Planet', s.planet, `${t.planetPctOfCollected || 0}% of ${money(b.collected)} collected, to give this month`],
    ['savings', 'Savings reserve', s.savings, `${t.savingsPctOfCollected || 0}% of ${money(b.collected)} collected, toward ${t.savingsGoalMonths || 3} months of running costs`],
    ['bonus', 'Bonus fund', s.bonus, `${t.bonusPctOfProfit || 0}% of ${money(b.profit)} profit before owner distributions`],
  ]
  // Each set-aside can be skipped on its own (Matt, 2026-11-02: "in case I want to do some but not all").
  const skipped = new Set(skip || [])
  const toggle = (key) => setSkip(skipped.has(key) ? [...skipped].filter(k => k !== key) : [...skipped, key])
  const allSkipped = lines.every(l => skipped.has(l[0]))
  const total = Math.round(lines.filter(l => !skipped.has(l[0])).reduce((x, l) => x + (l[2] || 0), 0) * 100) / 100
  const r = d.reserves
  const after = Math.round((r.total + total) * 100) / 100
  return (
    <div style={C.card}>
      <div style={stepH}>{num(6, true)}<span>What closing {mo} sets aside</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 13.5, ...C.muted, marginTop: 6, lineHeight: 1.5 }}>
          {mo}: collected {money(b.collected)}, spent {money(b.spent)} running the firm (payroll included), so profit {money(b.profit)}. Owner distributions ({money(b.distributions)}) are not an expense and don't change it.
          These are tracked inside savings until they have accounts of their own. Untick any you don't want to set aside for {mo}; its figure is kept with the month for the record.
        </div>
        <div style={{ marginTop: 10 }}>
          {lines.map(([key, name, amt, how]) => {
            const off = skipped.has(key)
            return (
              <label key={key} style={{ display: 'grid', gridTemplateColumns: '22px 170px 1fr 110px', columnGap: 12, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)', fontSize: 14, alignItems: 'baseline', cursor: 'pointer' }}>
                <input type="checkbox" checked={!off} onChange={() => toggle(key)} />
                <span style={{ fontWeight: 600, opacity: off ? 0.5 : 1 }}>{name}</span>
                <span style={{ fontSize: 13, ...C.muted, opacity: off ? 0.6 : 1 }}>{off ? 'Skipped for ' + mo + '. ' : ''}{how}</span>
                <span style={{ ...C.mono, textAlign: 'right', textDecoration: off ? 'line-through' : 'none', opacity: off ? 0.5 : 1 }}>{money(amt || 0)}</span>
              </label>
            )
          })}
          <div style={{ display: 'grid', gridTemplateColumns: '22px 170px 1fr 110px', columnGap: 12, padding: '8px 0', fontSize: 14, alignItems: 'baseline' }}>
            <span /><span style={{ fontWeight: 600 }}>From {mo}</span>
            <span><button style={{ ...C.link, fontSize: 13 }} onClick={() => setSkip(allSkipped ? [] : lines.map(l => l[0]))}>{allSkipped ? 'Set aside all of them' : 'Skip all for ' + mo}</button></span>
            <span style={{ ...C.mono, textAlign: 'right', fontWeight: 600 }}>{money(total)}</span>
          </div>
        </div>
        <div style={{ fontSize: 14, marginTop: 6, lineHeight: 1.55 }}>
          After closing, the set-asides add up to <b>{money(after)}</b>{r.savings
            ? <>. Chase savings held {money(r.savings.balance)} on {fullDate(r.savings.asOf)}: {after <= r.savings.balance
              ? <span style={{ color: C.good }}>enough to cover them, with {money(r.savings.balance - after)} more.</span>
              : <span style={{ color: C.bad }}>{money(after - r.savings.balance)} short. Move that much from checking to savings.</span>}</>
            : '. Type a savings statement balance to see whether savings covers them.'}
        </div>
        {r.total === 0 && <div style={{ fontSize: 13, ...C.muted, marginTop: 4 }}>This is the first month tracked; nothing from earlier months is counted.</div>}
      </div>
    </div>
  )
}

/**
 * The set-asides on the Books home (Matt, 2026-11-02: "track internally and watch the real numbers
 * available in the account to ensure accountability"). Each bucket's balance, the total beside the
 * savings account's real balance, and a form for money taken out (a tax payment, the Planet gift).
 */
function ReservesCard() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [hist, setHist] = useState(null)   // bucket key whose history is open, or 'all'
  useEffect(() => { getReserves().then(setD).catch(e => setErr(e.message)) }, [])
  if (!d) return err ? <div style={{ ...C.card, color: C.bad, fontSize: 13.5 }}>{err}</div> : null
  const save = async () => {
    setBusy(true); setErr('')
    try { setD(await moveReserve(form.direction, form.bucket, form.amount, form.date, form.note)); setForm(null) } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const open = (direction) => setForm({ direction, bucket: (d.buckets[0] || {}).key || 'tax', amount: '', date: new Date().toISOString().slice(0, 10), note: '' })
  const inp = { ...C.mono, fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }
  const rows = (d.history || []).filter(x => hist === 'all' || x.bucket === hist)
  const row = { display: 'grid', gridTemplateColumns: '1fr 110px 70px', columnGap: 12, alignItems: 'baseline', padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)', fontSize: 14 }
  return (
    <div style={C.card}>
      <div style={{ ...C.h, marginBottom: 4 }}>Set-asides, held in savings</div>
      {d.total > 0 && (
        <div style={{ margin: '6px 0 12px', padding: '12px 14px', borderRadius: 8, border: '0.5px solid ' + (d.floorRoom != null && d.floorRoom < 0 ? C.bad : 'var(--aj-border-mid)'), background: 'var(--aj-bg-subtle)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Keep savings at or above</span>
            <span style={{ ...C.mono, fontSize: 20, fontWeight: 600 }}>{money(d.floor)}</span>
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 4, color: d.floorRoom != null && d.floorRoom < 0 ? C.bad : 'var(--aj-text-mid)' }}>
            {d.savingsNow
              ? (d.floorRoom < 0
                ? `Savings held ${money(d.savingsNow.balance)} on ${fullDate(d.savingsNow.asOf)} (the last ${d.savingsNow.from}), ${money(-d.floorRoom)} below the set-asides. Move ${money(-d.floorRoom)} from checking to savings.`
                : `Savings held ${money(d.savingsNow.balance)} on ${fullDate(d.savingsNow.asOf)} (the last ${d.savingsNow.from}), ${money(d.floorRoom)} above it. Don't let a transfer out of savings take it below.`)
              : 'The set-asides total. Savings shows here once a savings download or statement is in.'}
            {' '}A Chase low-balance alert on savings at this amount warns you between closes; it changes after each close and each move below.
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, ...C.muted, lineHeight: 1.5, marginBottom: 8 }}>
        Separate accounts in name only: the money is in Chase savings. Each one's balance is what closes set aside, plus anything added, less anything taken out.
      </div>
      {d.buckets.map(b => (
        <div key={b.key} style={row}>
          <span>{b.name}{(b.added > 0 || b.used > 0) && <span style={{ fontSize: 12.5, ...C.muted }}> · {money(b.setAside)} from closes{b.added > 0 ? `, ${money(b.added)} added` : ''}{b.used > 0 ? `, ${money(b.used)} taken out` : ''}</span>}</span>
          <span style={{ ...C.mono, textAlign: 'right' }}>{money(b.balance)}</span>
          <span style={{ textAlign: 'right' }}><button style={{ ...C.link, fontSize: 12.5 }} onClick={() => setHist(hist === b.key ? null : b.key)}>{hist === b.key ? 'Hide' : 'History'}</button></span>
        </div>
      ))}
      <div style={{ ...row, borderBottom: 'none', fontWeight: 600 }}>
        <span>Set aside in all</span><span style={{ ...C.mono, textAlign: 'right' }}>{money(d.total)}</span>
        <span style={{ textAlign: 'right' }}><button style={{ ...C.link, fontSize: 12.5, fontWeight: 400 }} onClick={() => setHist(hist === 'all' ? null : 'all')}>{hist === 'all' ? 'Hide' : 'History'}</button></span>
      </div>

      {hist && (
        <div style={{ margin: '4px 0 10px', padding: '8px 12px', background: 'var(--aj-bg-subtle)', borderRadius: 6, fontSize: 13 }}>
          {rows.length ? rows.map(x => (
            <div key={x.id} style={{ display: 'grid', gridTemplateColumns: '58px 1fr 100px', columnGap: 10, padding: '4px 0', alignItems: 'baseline' }}>
              <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(x.date)}</span>
              <span>{hist === 'all' && <b style={{ fontWeight: 600 }}>{x.name} · </b>}{x.label}{x.why ? <span style={C.muted}>: {x.why}</span> : null}</span>
              <span style={{ ...C.mono, textAlign: 'right', color: x.sign < 0 ? C.bad : C.good }}>{x.sign < 0 ? '−' : '+'}{money(x.amount)}</span>
            </div>
          )) : <div style={C.muted}>Nothing has moved yet. The first close that sets aside adds the month's amounts here.</div>}
        </div>
      )}

      <div style={{ marginTop: 6, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)', fontSize: 14, lineHeight: 1.6 }}>
        {d.cash ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Cash in the bank on {fullDate(d.cash.asOf)} <span style={{ fontSize: 12.5, ...C.muted }}>(checking {money(d.cash.checking)}, savings {money(d.cash.savings)}, from the statements)</span></span><span style={C.mono}>{money(d.cash.total)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Less the set-asides</span><span style={C.mono}>−{money(d.total)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}><span>Available, not spoken for</span><span style={{ ...C.mono, color: d.available < 0 ? C.bad : 'var(--aj-text)' }}>{money(d.available)}</span></div>
            <div style={{ fontSize: 13, marginTop: 4, color: d.savingsBeyond < 0 ? C.bad : 'var(--aj-text-mid)' }}>
              {d.savingsBeyond < 0
                ? `Savings holds ${money(-d.savingsBeyond)} less than the set-asides. To make them real, move that much from checking to savings.`
                : `Savings holds ${money(d.savingsBeyond)} more than the set-asides. That much of savings is free to use.`}
            </div>
          </>
        ) : <span style={C.muted}>Cash shows once both statements are typed in a close.</span>}
      </div>

      {form ? (
        <div style={{ marginTop: 12, padding: '10px 12px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 13.5 }}>
          <b style={{ width: '100%' }}>{form.direction === 'out' ? 'Money taken out of a set-aside' : 'Money added to a set-aside'}</b>
          <select value={form.bucket} onChange={e => setForm({ ...form, bucket: e.target.value })} style={inp}>
            {d.buckets.map(b => <option key={b.key} value={b.key}>{b.name} ({money(b.balance)})</option>)}
          </select>
          <input placeholder="Amount" inputMode="decimal" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value.replace(/[$,]/g, '') })} style={{ ...inp, width: 100 }} />
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inp} />
          <input placeholder={form.direction === 'out' ? 'Why (e.g. Q4 estimated tax, Matt and Emily)' : 'Why (e.g. extra toward last year\u2019s taxes)'} value={form.note}
            onChange={e => setForm({ ...form, note: e.target.value })} style={{ ...inp, fontFamily: '"DM Sans", sans-serif', flex: '1 1 240px' }} />
          <button style={C.quiet} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Record it'}</button>
          <button style={C.link} onClick={() => setForm(null)}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 13 }}>
          <button style={C.link} onClick={() => open('out')}>Money taken out (a tax payment, the Planet gift, bonuses)</button>
          <button style={C.link} onClick={() => open('in')}>Money added</button>
        </div>
      )}
      {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  )
}

/**
 * The CPA's reports, as a step of the close (Matt, 2026-11-03: "always forced to stay up to date on
 * reporting"). Shown only when a quarter or year that has ended by this month (this month's own
 * quarter included) is missing one of its four reports; the close can't finish until each is added.
 */
function CpaOwedStep({ month, num, stepH, onOwed }) {
  const [owed, setOwed] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const load = useCallback(() => getCpaOwed(month.end).then(r => { setOwed(r.owed); onOwed(r.owed.reduce((t, p) => t + p.missing.length, 0)) })
    .catch(e => { setErr(e.message); onOwed(0) }), [month.end, onOwed])
  useEffect(() => { load() }, [load])
  if (!owed || !owed.length) return err ? <div style={{ ...C.card, color: C.bad, fontSize: 13.5 }}>Could not check the CPA reports: {err}</div> : null
  const add = async (p, title, file) => {
    if (!file) return
    setBusy(p.start + title); setErr('')
    try {
      await addCpaFile(file, { title, kind: p.kind, year: p.start.slice(0, 4), quarter: Math.floor(Number(p.start.slice(5, 7)) / 3) + 1 })
      await load()
    } catch (e) { setErr(e.message) } finally { setBusy('') }
  }
  return (
    <div style={{ ...C.card, borderColor: C.bad }}>
      <div style={stepH}>{num(7, false)}<span>Add the CPA's reports</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 13.5, ...C.muted, marginTop: 8, lineHeight: 1.5 }}>
          These periods have ended and the CPA reports center is missing their reports. In Core's Reports, set each one to accrual basis and the dates beside it, export it as a PDF and add it here. They go straight into his center.
        </div>
        {owed.map(p => (
          <div key={p.kind + p.start} style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>{p.label} <span style={{ fontWeight: 400, fontSize: 13, ...C.muted }}>({p.start.slice(5) === '01-01' && p.kind === 'year' ? 'Jan 1 to Dec 31' : shortDate(p.start) + ' to ' + shortDate(p.end)})</span></div>
            {p.missing.map(t => (
              <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 0', fontSize: 14 }}>
                <span style={{ width: 190 }}>{t}</span>
                <span style={{ width: 230, fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{coreReportDates(t, p)}</span>
                {busy === p.start + t ? <span style={{ fontSize: 13, ...C.muted }}>Adding…</span>
                  : <input type="file" onChange={e => add(p, t, e.target.files[0])} style={{ fontSize: 12.5 }} />}
              </div>
            ))}
          </div>
        ))}
        {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  )
}

/**
 * THE CPA REPORTS CENTER, Bookkeeping side (Matt, 2026-11-03; docs/CPA_REPORTS.md): who has the
 * link, his PIN, the reports in it, and every visit and download. The closed months' books are
 * there on their own; quarterly and annual reports are exported from Core and added here.
 */
const CPA_TITLES = ['General Ledger', 'Balance Sheet', 'Profit and Loss', 'Statement of Cash Flows']
const CPA_EVENT = { open: 'opened the center', pin_ok: 'entered the right PIN', pin_wrong: 'entered a wrong PIN', locked: 'locked the link (5 wrong PINs)', download: 'downloaded', package: 'downloaded the books for', period: 'downloaded everything for the period starting' }
function CpaCenterCard() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const [who, setWho] = useState({ name: '', email: '', firm: '' })
  const [showPin, setShowPin] = useState(false)
  const [copied, setCopied] = useState('')
  const yr = new Date().getFullYear()
  const [f, setF] = useState({ title: 'Balance Sheet', kind: 'quarter', year: String(yr), quarter: String(Math.max(1, Math.floor(new Date().getMonth() / 3))), month: '1', file: null })
  const fileRef = useRef(null)
  const [logOpen, setLogOpen] = useState(false)
  useEffect(() => { getCpaCenter().then(setD).catch(e => setErr(e.message)) }, [])
  const run = async (k, fn) => { setBusy(k); setErr(''); try { const r = await fn(); if (r && r.access) setD(r); return r } catch (e) { setErr(e.message) } finally { setBusy('') } }
  const copy = async (text, k) => { try { await navigator.clipboard.writeText(text); setCopied(k); setTimeout(() => setCopied(''), 1500) } catch { /* nothing */ } }
  if (!d) return err ? <div style={{ ...C.card, color: C.bad, fontSize: 13.5 }}>{err}</div> : null
  const inp = { fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif' }
  const a = d.access[0]
  const stamp = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
  const nameOf = (id) => (d.access.find(x => x.id === id) || {}).name || 'Someone'
  const years = [...new Set(d.files.map(x => x.year))]
  const upload = () => run('upload', async () => {
    if (!f.file) throw new Error('Pick the file.')
    const r = await addCpaFile(f.file, { title: f.title, kind: f.kind, year: f.year, quarter: f.quarter, month: f.month })
    setF({ ...f, file: null }); if (fileRef.current) fileRef.current.value = ''
    return r
  })
  return (
    <div style={C.card}>
      <div style={{ ...C.h, marginBottom: 4 }}>Reports for the CPA</div>
      <div style={{ fontSize: 13, ...C.muted, lineHeight: 1.5, marginBottom: 12 }}>
        One link and a PIN for the firm's accountant. He sees the reports added here and every closed month's books, nothing else. Five wrong PINs lock the link until you give him a new PIN.
      </div>

      {!a ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Name" value={who.name} onChange={e => setWho({ ...who, name: e.target.value })} style={{ ...inp, flex: '1 1 160px' }} />
          <input placeholder="Email" value={who.email} onChange={e => setWho({ ...who, email: e.target.value })} style={{ ...inp, flex: '1 1 180px' }} />
          <input placeholder="Firm" value={who.firm} onChange={e => setWho({ ...who, firm: e.target.value })} style={{ ...inp, flex: '1 1 140px' }} />
          <button style={C.quiet} disabled={busy === 'add'} onClick={() => run('add', () => cpaAction('add', who))}>{busy === 'add' ? 'Making it…' : 'Make his link and PIN'}</button>
        </div>
      ) : (
        <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--aj-bg-subtle)', fontSize: 14, lineHeight: 1.6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <b>{a.name}{a.firm ? <span style={{ fontWeight: 400, ...C.muted }}> · {a.firm}</span> : null}</b>
            <span style={{ fontSize: 13, color: a.locked || a.status === 'off' ? C.bad : C.good }}>
              {a.status === 'off' ? 'Turned off' : a.locked ? 'Locked after 5 wrong PINs' : a.lastSeen ? 'Last opened ' + stamp(a.lastSeen) : 'Not opened yet'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', marginTop: 4 }}>
            <span>Link <button style={{ ...C.link, fontSize: 13 }} onClick={() => copy(a.link, 'link')}>{copied === 'link' ? 'Copied' : 'Copy link'}</button></span>
            <span>PIN <span style={C.mono}>{showPin ? a.pin : '••••••'}</span> <button style={{ ...C.link, fontSize: 13 }} onClick={() => setShowPin(v => !v)}>{showPin ? 'Hide' : 'Show'}</button>
              {showPin && <> · <button style={{ ...C.link, fontSize: 13 }} onClick={() => copy(a.pin, 'pin')}>{copied === 'pin' ? 'Copied' : 'Copy'}</button></>}</span>
          </div>
          <div style={{ fontSize: 12.5, ...C.muted, marginTop: 4 }}>Send the link by email and the PIN another way (a text or a call), never in the same message.</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
            <button style={C.link} disabled={!!busy} onClick={async () => {
              if (!(await confirmDialog({ title: a.locked ? 'Unlock with a new PIN?' : 'Give him a new PIN?', confirmLabel: 'Make a new PIN',
                message: 'His current PIN stops working at once, and he is asked for the new one next time. Send it to him by text or call.' }))) return
              await run('pin', () => cpaAction('newPin', { id: a.id })); setShowPin(true)
            }}>{a.locked ? 'Unlock: new PIN' : 'New PIN'}</button>
            <button style={C.link} disabled={!!busy} onClick={async () => {
              if (!(await confirmDialog({ title: 'Replace his link?', confirmLabel: 'New link and PIN', danger: true,
                message: 'The old link stops working at once (use this if it was forwarded). He needs the new link and the new PIN.' }))) return
              await run('link', () => cpaAction('newLink', { id: a.id })); setShowPin(true)
            }}>New link and PIN</button>
            <button style={C.link} disabled={!!busy} onClick={() => run('off', () => cpaAction(a.status === 'off' ? 'on' : 'off', { id: a.id }))}>{a.status === 'off' ? 'Turn it back on' : 'Turn it off'}</button>
          </div>
        </div>
      )}

      {d.owed && d.owed.length > 0 && (
        <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, border: '0.5px solid ' + C.bad, fontSize: 13.5, lineHeight: 1.55 }}>
          <b style={{ color: C.bad }}>Owed to the CPA. The next close can't finish until these are in.</b>
          {d.owed.map(p => (
            <div key={p.kind + p.start} style={{ marginTop: 4 }}>
              <div>{p.label}</div>
              {p.missing.map(t => (
                <div key={t} style={{ display: 'flex', gap: 10, paddingLeft: 12 }}>
                  <button style={{ ...C.link, fontSize: 13.5, width: 180, textAlign: 'left' }} onClick={() => {
                    setF({ ...f, title: t, kind: p.kind, year: p.start.slice(0, 4), quarter: String(Math.floor(Number(p.start.slice(5, 7)) / 3) + 1), file: null })
                    if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click() }
                  }}>{t}</button>
                  <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{coreReportDates(t, p)}</span>
                </div>
              ))}
            </div>
          ))}
          <div style={{ fontSize: 12.5, ...C.muted, marginTop: 4 }}>In Core's Reports, set each one to accrual basis and the dates beside it, then export it as a PDF. Clicking a name sets the form below and opens the file picker.</div>
        </div>
      )}

      <div style={{ ...C.h, fontSize: 10.5, margin: '18px 0 6px' }}>Quarterly and annual reports</div>
      {years.length === 0 && <div style={{ fontSize: 13.5, ...C.muted }}>None yet. Export them from Core (Reports, accrual basis) and add them below.</div>}
      {years.map(y => (
        <div key={y} style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, ...C.muted, marginTop: 6 }}>{y}</div>
          {d.files.filter(x => x.year === y).map(x => (
            <div key={x.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 12, padding: '6px 0', borderBottom: '0.5px solid var(--aj-border)', fontSize: 14 }}>
              <span>{x.title}<span style={{ fontSize: 12.5, ...C.muted }}> · {x.period} · {x.filename}</span></span>
              <span style={{ display: 'flex', gap: 12, fontSize: 12.5 }}>
                <button style={C.link} onClick={() => run('open', async () => { const r = await cpaAction('openFile', { id: x.id }); window.open(r.url, '_blank') })}>Open</button>
                <button style={C.link} onClick={async () => { if (!(await confirmDialog({ title: 'Take this report out?', message: `${x.title}, ${x.period}. He can no longer download it.`, confirmLabel: 'Take it out', danger: true }))) return; run('rm', () => cpaAction('removeFile', { id: x.id })) }}>Remove</button>
              </span>
            </div>
          ))}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        <select value={CPA_TITLES.includes(f.title) ? f.title : '__other'} onChange={e => setF({ ...f, title: e.target.value === '__other' ? '' : e.target.value })} style={inp}>
          {CPA_TITLES.map(x => <option key={x}>{x}</option>)}
          <option value="__other">Other…</option>
        </select>
        {!CPA_TITLES.includes(f.title) && <input placeholder="Report name" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} style={{ ...inp, width: 160 }} />}
        <select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} style={inp}>
          <option value="quarter">Quarter</option><option value="year">Year</option><option value="month">Month</option><option value="other">Other</option>
        </select>
        {f.kind === 'quarter' && <select value={f.quarter} onChange={e => setF({ ...f, quarter: e.target.value })} style={inp}>{[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}</select>}
        {f.kind === 'month' && <select value={f.month} onChange={e => setF({ ...f, month: e.target.value })} style={inp}>{Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}</option>)}</select>}
        <select value={f.year} onChange={e => setF({ ...f, year: e.target.value })} style={inp}>{[yr, yr - 1, yr - 2].map(y => <option key={y} value={String(y)}>{y}</option>)}</select>
        <input ref={fileRef} type="file" onChange={e => setF({ ...f, file: e.target.files[0] || null })} style={{ fontSize: 12.5 }} />
        <button style={C.quiet} disabled={busy === 'upload' || !f.file} onClick={upload}>{busy === 'upload' ? 'Adding…' : 'Add report'}</button>
      </div>
      <div style={{ fontSize: 12.5, ...C.muted, marginTop: 6 }}>Each closed month's books (transactions by account, and the tie-out) are in the center automatically; you don't add those.</div>

      {d.events.length > 0 && (
        <div style={{ marginTop: 14, fontSize: 13 }}>
          <button style={C.link} onClick={() => setLogOpen(v => !v)}>{logOpen ? 'Hide' : 'Show'} what he has opened ({d.events.length})</button>
          {logOpen && d.events.slice(0, 30).map(e => (
            <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', columnGap: 10, padding: '3px 0', color: e.kind === 'pin_wrong' || e.kind === 'locked' ? C.bad : 'var(--aj-text)' }}>
              <span style={{ ...C.mono, fontSize: 12, ...C.muted }}>{stamp(e.at)}</span>
              <span>{nameOf(e.accessId)} {CPA_EVENT[e.kind] || e.kind}{e.file ? ' ' + e.file : ''}{e.month ? ' ' + new Date(e.month + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : ''}{e.ip ? <span style={{ fontSize: 12, ...C.muted }}> · {e.ip}</span> : null}</span>
            </div>
          ))}
        </div>
      )}
      {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  )
}

/**
 * Reimbursables for the next bills (Matt, 2026-11-02): every billable expense in Core no invoice has
 * picked up, by project. The month's new ones are marked; ones more than 60 days old are called out,
 * since the bill they should have gone on has already gone; pre-2024 ones sit apart.
 */
function ReimbursablesCard({ month, num, stepH }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState('')
  const load = useCallback(() => getCloseReimbursables().then(setD).catch(e => setErr(e.message)), [])
  useEffect(() => { load() }, [load])
  const dontBill = async (i, project) => {
    if (!(await confirmDialog({ title: "Don't bill this expense?", confirmLabel: "Don't bill it",
      message: `${i.what} from ${i.from || 'the vendor'}, ${money(i.charge)}, on ${project}. Core marks it not billable, so no invoice will carry it and it leaves this list. The cost stays in the books.` }))) return
    setBusy(i.id); setErr('')
    try { await markExpenseNotBillable(i.id); await load() } catch (e) { setErr(e.message) } finally { setBusy('') }
  }
  const mo = monthOnly(month.name)
  if (!d) return <div style={C.card}><div style={stepH}>{num(5, false)}<span>Reimbursables for the next bills</span></div>
    <div style={{ marginLeft: 38, marginTop: 8, fontSize: 13.5, color: err ? C.bad : 'var(--aj-text-mid)' }}>{err || 'Reading billable expenses from Core…'}</div></div>
  const items = d.projects.flatMap(p => p.items)
  const nThis = items.filter(i => i.when === 'this month').length
  const late = items.filter(i => i.when === 'late')
  const lateTotal = Math.round(late.reduce((t, i) => t + i.charge, 0) * 100) / 100
  const tag = (i) => i.when === 'this month' ? ['recorded in ' + mo, C.good] : [i.months <= 1 ? 'recorded last month' : 'recorded ' + i.months + ' months ago', i.when === 'late' ? '#b8540a' : 'var(--aj-text-muted)']
  const current = d.projects.map(p => ({ ...p, items: p.items.filter(i => i.when !== 'old') })).filter(p => p.items.length)
  const oldOnes = d.projects.map(p => ({ ...p, items: p.items.filter(i => i.when === 'old') })).filter(p => p.items.length)
  const grid = { display: 'grid', gridTemplateColumns: '64px minmax(180px,1.3fr) minmax(140px,1fr) 140px 90px 92px', columnGap: 12, alignItems: 'baseline' }
  const right = { ...C.mono, textAlign: 'right' }
  const list = (groups) => groups.map(p => (
    <div key={p.project} style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600, fontSize: 13.5 }}>
        <span>{p.project}{p.client ? <span style={{ fontWeight: 400, ...C.muted }}> · {p.client}</span> : null}</span>
        <span style={C.mono}>{money(p.items.reduce((t, i) => t + i.charge, 0))}</span>
      </div>
      {p.items.map(i => (
        <div key={i.id} style={{ ...grid, fontSize: 13, padding: '4px 0', borderBottom: '0.5px solid var(--aj-border)', opacity: busy === i.id ? 0.5 : 1 }}>
          <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(i.date)}{i.date.slice(0, 4) !== month.first.slice(0, 4) ? ' ' + i.date.slice(2, 4) : ''}</span>
          <span>{i.what}{i.description ? <span style={C.muted}> · {i.description}</span> : null}</span>
          <span style={C.muted}>{i.from}</span>
          <span style={{ fontSize: 12.5, color: tag(i)[1] }}>{tag(i)[0]}</span>
          <span style={right}>{money(i.charge)}</span>
          <span style={{ textAlign: 'right' }}><button style={{ ...C.link, fontSize: 12.5 }} disabled={!!busy} onClick={() => dontBill(i, p.project)}>Don't bill it</button></span>
        </div>
      ))}
    </div>
  ))
  return (
    <div style={C.card}>
      <div style={stepH}>{num(5, !late.length)}<span>Reimbursables for the next bills</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.55 }}>
          {d.count} expense{d.count === 1 ? ' is' : 's are'} marked billable in Core and on no invoice yet: {money(d.total)} as they would be billed (cost plus markup). Each goes on its project's next invoice when that project is billed.{' '}
          <button style={{ ...C.link, fontSize: 13 }} onClick={() => setOpen(v => !v)}>{open ? 'Hide the list' : 'Show the list'}</button>
        </div>
        <div style={{ fontSize: 13.5, marginTop: 6, lineHeight: 1.55 }}>
          {nThis > 0 && <div><span style={{ color: C.good }}>{nThis} recorded in {mo}.</span> Nothing to do: they go on November's bills.</div>}
          {late.length > 0 && <div><b style={{ color: '#b8540a' }}>{late.length} recorded more than two months ago, {money(lateTotal)}.</b> Bills have gone out since they were recorded without them (Forsyth was billed in September; its April permit fee was not on it). Why they were left off is not known yet. To do, in the list: leave each one the client should pay (it stays here until an invoice carries it), and press <b>Don't bill it</b> on any the client won't be charged.</div>}
        </div>
        {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 6 }}>{err}</div>}
        {open && (
          <div style={{ marginTop: 4 }}>
            {list(current)}
            {oldOnes.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13, ...C.muted }}>Recorded before 2024, {money(d.oldTotal)}. These sit on projects that have not billed since. Press Don't bill it on any that won't be billed, and they leave this list.</div>
                {list(oldOnes)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The cleared list (Matt, 2026-11-02, after Core's reconciliation report): each account's bank lines
 * for the month in date order, what each one is in Core, and the running balance from the statement's
 * beginning balance to its ending. A deposit opens to the client payments and invoices inside it.
 */
function ClearedCard({ month, num, stepH }) {
  const [reg, setReg] = useState(null)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState({})
  useEffect(() => { getCloseRegister().then(setReg).catch(e => setErr(e.message)) }, [])
  const r2 = n => Math.round(n * 100) / 100
  const mo = monthOnly(month.name)
  if (!reg) return <div style={C.card}><div style={stepH}>{num(3, false)}<span>Review what cleared</span></div>
    <div style={{ marginLeft: 38, marginTop: 8, fontSize: 13.5, color: err ? C.bad : 'var(--aj-text-mid)' }}>{err || 'Reading each transaction from Core…'}</div></div>
  const accts = reg.accounts.map(a => {
    let bal = a.beginning
    const rows = a.rows.map(x => { if (bal != null) bal = r2(bal + x.amount); return { ...x, bal } })
    const end = bal
    const ties = a.beginning != null && a.ending != null && end != null && Math.round(end * 100) === Math.round(a.ending * 100)
    return { ...a, rows, end, ties, missing: rows.filter(x => !x.inCore).length,
      inTotal: r2(a.rows.filter(x => x.amount > 0).reduce((t, x) => t + x.amount, 0)),
      outTotal: r2(a.rows.filter(x => x.amount < 0).reduce((t, x) => t + x.amount, 0)) }
  })
  const allOk = accts.every(a => a.ties && !a.missing)
  const grid = { display: 'grid', gridTemplateColumns: '58px minmax(220px,1.1fr) minmax(260px,1.4fr) 110px 120px', columnGap: 14, alignItems: 'baseline' }
  const right = { ...C.mono, textAlign: 'right' }
  return (
    <div style={C.card}>
      <div style={stepH}>{num(3, allOk)}<span>Review what cleared</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 13.5, ...C.muted, marginTop: 6, lineHeight: 1.5 }}>
          Every {mo} line on each statement, what it is in Core, and the balance after it, from the statement's beginning balance to its ending.
        </div>
        {accts.map(a => (
          <div key={a.code} style={{ marginTop: 16, paddingTop: 12, borderTop: '0.5px solid var(--aj-border)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <b style={{ fontSize: 15 }}>{a.name}</b>
              <span style={{ fontSize: 13.5, ...C.muted }}>
                {a.rows.length} transaction{a.rows.length === 1 ? '' : 's'} · in {money(a.inTotal)} · out {money(-a.outTotal)}
              </span>
              <span style={{ fontSize: 13.5, color: a.missing ? C.bad : a.ties ? C.good : 'var(--aj-text-mid)' }}>
                {a.missing ? `${a.missing} not in Core` : a.ties ? '✓ all in Core, ends on the statement balance'
                  : a.beginning == null ? 'Type the beginning balance to see the running balance' : `ends at ${money(a.end)}, statement says ${a.ending == null ? '—' : money(a.ending)}`}
              </span>
              <button style={{ ...C.link, fontSize: 13, marginLeft: 'auto' }} onClick={() => setOpen(o => ({ ...o, [a.code]: !o[a.code] }))}>
                {open[a.code] ? 'Hide the list' : 'Show the list'}
              </button>
            </div>
            {open[a.code] && (
              <div style={{ marginTop: 10, fontSize: 13.5 }}>
                <div style={{ ...grid, ...C.muted, fontSize: 12, paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border)' }}>
                  <span>Date</span><span>On the statement</span><span>In Core</span><span style={{ textAlign: 'right' }}>Amount</span><span style={{ textAlign: 'right' }}>Balance</span>
                </div>
                <div style={{ ...grid, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                  <span /><span style={{ fontWeight: 600 }}>Beginning balance</span><span /><span /><span style={right}>{a.beginning == null ? '—' : money(a.beginning)}</span>
                </div>
                {a.rows.map(x => (
                  <div key={x.id} style={{ padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                    <div style={grid}>
                      <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(x.date)}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.bank}>{x.bank}</span>
                      <span style={{ color: x.inCore ? 'var(--aj-text)' : C.bad }}>
                        {x.inCore ? <>{x.core.kind && <span style={{ ...C.muted }}>{x.core.kind}: </span>}{x.core.text}</> : 'Not in Core: go back to Decide, where it is a card to settle'}
                      </span>
                      <span style={{ ...right, color: x.amount > 0 ? C.good : 'var(--aj-text)' }}>{x.amount > 0 ? '+' : ''}{money(x.amount)}</span>
                      <span style={{ ...right, ...C.muted }}>{x.bal == null ? '' : money(x.bal)}</span>
                    </div>
                    {x.core.lines.map((l, k) => (
                      <div key={k} style={{ ...grid, fontSize: 12.5, ...C.muted, marginTop: 3 }}>
                        <span /><span /><span style={{ paddingLeft: 12 }}>{l.text}</span><span style={right}>{money(l.amount)}</span><span />
                      </div>
                    ))}
                  </div>
                ))}
                <div style={{ ...grid, padding: '8px 0' }}>
                  <span /><span style={{ fontWeight: 600 }}>Ending balance</span>
                  <span style={{ color: a.ties ? C.good : C.bad, fontSize: 13 }}>{a.ending == null ? '' : a.ties ? '✓ same as the statement' : 'statement says ' + money(a.ending)}</span>
                  <span /><span style={{ ...right, fontWeight: 600 }}>{a.end == null ? '—' : money(a.end)}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The statements step (Matt, 2026-10-29): the bank's own statement open beside the numbers. One panel
 * per account: upload the month's PDF, read it here, type its beginning and ending balance, and see it
 * against the downloads and Core. The month closes when all three agree, or with a note saying why.
 */
function StatementsCard({ onChange, step, stepH }) {
  const [st, setSt] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const load = useCallback(async () => {
    try { const s = await getCloseStatements(); setSt(s); onChange && onChange(s) } catch (e) { setErr(e.message) }
  }, [onChange])
  useEffect(() => { load() }, [load])
  const act = async (code, fn) => {
    setBusy(code); setErr('')
    try { const s = await fn(); setSt(s); onChange && onChange(s) } catch (e) { setErr(e.message) } finally { setBusy('') }
  }
  if (!st) return <div style={C.card}>{err ? <span style={{ color: C.bad }}>{err}</span> : <span style={C.muted}>Reading the statement balances…</span>}</div>
  const same = (x, y) => x != null && y != null && Math.round(x * 100) === Math.round(y * 100)
  const colH = { ...C.muted, fontSize: 12.5, textAlign: 'right' }
  return (
    <div style={C.card}>
      <div style={stepH}>{step}<span>Type the balances from each Chase statement</span></div>
      <div style={{ marginLeft: 38 }}>
        <div style={{ fontSize: 13.5, ...C.muted, marginTop: 6, lineHeight: 1.5 }}>
          Open each account's {st.month.name} statement on chase.com and type the beginning and ending balance it shows. Attaching the PDF is optional.
        </div>
        {st.error && <div style={{ color: C.bad, fontSize: 13, marginTop: 8 }}>Could not read Core's balances: {st.error}</div>}
        {st.accounts.map(a => (
          <div key={a.code} style={{ marginTop: 18, paddingTop: 14, borderTop: '0.5px solid var(--aj-border)', opacity: busy === a.code ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <b style={{ fontSize: 15 }}>{a.name}</b>
              <label style={{ ...C.link, cursor: 'pointer', fontSize: 13 }}>
                {a.filename ? 'Replace the attached statement' : 'Attach the PDF'}
                <input type="file" accept="application/pdf" style={{ display: 'none' }}
                  onChange={e => { const f = Array.from(e.target.files || [])[0]; e.target.value = ''; if (f) act(a.code, () => uploadStatement(a.code, f)) }} />
              </label>
            </div>
            {a.viewUrl && <iframe title={a.name + ' statement'} src={a.viewUrl} style={{ width: '100%', height: 420, border: '0.5px solid var(--aj-border)', borderRadius: 6, marginTop: 8, background: '#fff' }} />}
            <div style={{ display: 'grid', gridTemplateColumns: '90px 160px 150px 1fr', columnGap: 16, rowGap: 10, alignItems: 'center', marginTop: 10, fontSize: 14 }}>
              <span /><span style={colH}>Statement says</span><span style={colH}>Should be</span><span />
              {[['beginning', 'opening', 'Beginning'], ['ending', 'closing', 'Ending']].map(([k, field, label]) => {
                const r = a[k]
                const expected = same(r.downloads, r.core) ? r.downloads : null
                const typed = r.statement != null
                return [
                  <span key={k + 'l'}>{label}</span>,
                  <input key={a.code + k + (r.statement ?? '')} defaultValue={r.statement ?? ''} placeholder="Type it" inputMode="decimal"
                    onBlur={e => { const v = e.target.value.trim().replace(/[$,]/g, ''); if (v !== String(r.statement ?? '')) act(a.code, () => saveStatement(a.code, { [field]: v })) }}
                    style={{ width: '100%', boxSizing: 'border-box', textAlign: 'right', ...C.mono, fontSize: 14, padding: '7px 10px', border: '0.5px solid ' + (typed && !r.matches ? C.bad : 'var(--aj-border-mid)'), borderRadius: 4, background: 'var(--aj-bg-page)', color: 'var(--aj-text)' }} />,
                  <span key={k + 'e'} style={{ ...C.mono, textAlign: 'right' }}>
                    {expected != null ? money(expected) : <span style={{ color: C.bad, fontSize: 12.5, fontFamily: '"DM Sans", sans-serif' }}>bank {r.downloads == null ? '—' : money(r.downloads)} · Core {r.core == null ? '—' : money(r.core)}</span>}
                  </span>,
                  <span key={k + 's'} style={{ fontSize: 13.5, color: r.matches ? C.good : C.bad }}>
                    {!typed ? '' : r.matches ? (r.byTiming ? '✓ matches, after timing' : '✓ matches') : expected != null ? 'off by ' + money(Number(r.statement) - expected) : "doesn't match"}
                  </span>,
                  ...(r.byTiming && r.timing ? [<div key={k + 't'} style={{ gridColumn: '2 / span 3', fontSize: 12.5, ...C.muted, lineHeight: 1.5, marginTop: -4 }}>
                    Core is {money(Math.abs(r.timing.net))} {r.timing.net > 0 ? 'ahead of' : 'behind'} the bank only because of dates, nothing to fix:{' '}
                    {r.timing.items.map((i, n2) => <span key={n2}>{n2 ? '; ' : ''}{i.kind.toLowerCase()} {money(Math.abs(i.amount))}{i.who ? ' (' + String(i.who).replace(/;.*$/, '').slice(0, 40) + ')' : ''}, Core {shortDate(i.coreDate)}, bank {shortDate(i.bankDate)}{i.pending ? ' (still pending then)' : ''}</span>)}.
                  </div>] : []),
                ]
              })}
            </div>
          </div>
        ))}
        {err && <div style={{ color: C.bad, fontSize: 13, marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  )
}

function PostBar({ bar }) {
  const pct = bar.total ? Math.round(100 * bar.n / bar.total) : 0
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: C.good, transition: 'width .4s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, ...C.muted, marginTop: 6 }}>
        <span>{bar.n} of {bar.total} in Core</span><span>{pct}%</span>
      </div>
      <div style={{ fontSize: 12.5, ...C.muted, marginTop: 2 }}>Keep this window open. Core takes about a second for each one; if it asks to slow down, posting waits and carries on.</div>
    </div>
  )
}

function StoppedCard({ stopped, onCheck }) {
  const [raw, setRaw] = useState(false)
  return (
    <div style={{ ...C.card, borderColor: C.bad, overflowWrap: 'anywhere' }}>
      <div style={{ fontWeight: 600 }}>Stopped after {stopped.done} of {stopped.total}.</div>
      <div style={{ fontSize: 13.5, ...C.muted, marginTop: 4 }}>The {stopped.done} before it are in Core. Nothing after it was sent.</div>
      <div style={{ fontSize: 14, marginTop: 10, lineHeight: 1.5 }}>{stopped.why}</div>
      {stopped.detail && (
        <div style={{ marginTop: 8 }}>
          <button style={{ ...C.link, fontSize: 12.5 }} onClick={() => setRaw(v => !v)}>{raw ? "Hide Core's own words" : "Show Core's own words"}</button>
          {raw && <div style={{ ...C.mono, fontSize: 11.5, ...C.muted, marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{stopped.detail}</div>}
        </div>
      )}
      <div style={{ fontSize: 13.5, marginTop: 12, lineHeight: 1.5 }}><b>Next:</b> {/429|too many requests/i.test(String(stopped.why) + String(stopped.detail || ''))
        ? <>nothing to fix: Core only allows so many calls a minute. Wait a minute, then press <b>Check again</b>.</>
        : <>fix that in Core, then press <b>Check again</b>.</>} The posting button comes back with only what's left; nothing is sent twice.</div>
      <button style={{ ...C.quiet, marginTop: 10 }} onClick={onCheck}>Check again</button>
    </div>
  )
}

/** A Post line; with items it opens to the transactions behind it (Matt, 2026-11-02). */
function Line({ label, n, amount, plus, items }) {
  const [open, setOpen] = useState(false)
  const can = Array.isArray(items) && items.length > 0
  return (
    <div style={{ borderBottom: '0.5px solid var(--aj-border)' }}>
      <div onClick={() => can && setOpen(v => !v)} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', cursor: can ? 'pointer' : 'default' }}>
        <span>{can && <span style={{ display: 'inline-block', width: 14, ...C.muted, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>}
          {label} <span style={{ fontSize: 12.5, ...C.muted }}>· {n}</span></span>
        <span style={C.mono}>{plus ? '+' : ''}{money(amount)}</span>
      </div>
      {open && (
        <div style={{ padding: '0 0 10px 14px', fontSize: 13 }}>
          {[...items].sort((a, b) => String(a.date).localeCompare(String(b.date))).map((x, k) => (
            <div key={(x.id || '') + k} style={{ display: 'grid', gridTemplateColumns: '54px minmax(120px,1fr) minmax(140px,1.4fr) 100px', columnGap: 12, padding: '4px 0', alignItems: 'baseline' }}>
              <span style={{ ...C.mono, fontSize: 12.5, ...C.muted }}>{shortDate(x.date)}</span>
              <span>{x.who}</span>
              <span style={{ ...C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.what}>{x.note ? x.note + ' · ' : ''}{x.what}</span>
              <span style={{ ...C.mono, textAlign: 'right' }}>{plus ? '+' : ''}{money(x.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TieDetail({ tie }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
      {Object.values(tie.tie).filter(t => t.difference !== 0).map(t => (
        <div key={t.name} style={{ marginTop: 6 }}>
          <b>{t.name}</b>
          {(t.bankOnly || []).length > 0 && <div style={{ ...C.muted, marginTop: 4 }}>In the bank, not in Core</div>}
          {(t.bankOnly || []).map(x => (
            <div key={x.rowId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{shortDate(x.date)} · {x.what} <span style={{ ...C.muted }}>· {x.why}</span></span><span style={{ ...C.mono }}>{money(x.amount)}</span>
            </div>
          ))}
          {(t.coreOnly || []).length > 0 && <div style={{ ...C.muted, marginTop: 6 }}>In Core, not in the bank</div>}
          {(t.coreOnly || []).map(x => (
            <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{shortDate(x.date)} · {x.kind}{x.who ? ', ' + x.who : ''}</span><span style={{ ...C.mono }}>{money(x.amount)}</span>
            </div>
          ))}
          {t.unexplained !== 0 && <div style={{ color: C.bad, marginTop: 6 }}>{money(t.unexplained)} still unexplained after these: a linked entry whose amount differs from its bank line.</div>}
        </div>
      ))}
    </div>
  )
}

// ── done ────────────────────────────────────────────────────────────────────

function Done({ result, onHome }) {
  const r = result.receipt, s = result.setAside
  const [busy, setBusy] = useState(false)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 15, color: result.ties ? C.good : 'var(--aj-text-mid)' }}>
              {r.transactions} transactions · {result.ties ? 'ties to the bank ✓' : 'closed with your note'}
            </div>
            <div style={{ fontSize: 34, fontWeight: 600, marginTop: 4 }}>Books current through {fullDate(result.booksCurrentThrough)}</div>
          </div>
          <div style={C.card}>
            <div style={C.h}>The month</div>
            <div style={{ fontSize: 15, lineHeight: 1.6, marginTop: 8 }}>
              Collected {money(r.collected)}. Spent {money(r.spent)} running the firm, plus {money(r.distributions)} to the owners.
              {r.lateArrivals.length > 0 && <> {r.lateArrivals.length} late arrival{r.lateArrivals.length === 1 ? '' : 's'} filed into earlier months.</>}
            </div>
          </div>
          <div style={{ ...C.card, display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>CPA package for {result.month.name}</div>
              <div style={{ fontSize: 13, ...C.muted, marginTop: 3 }}>Every transaction with its account, the tie-out and the set-asides, as a spreadsheet.</div>
            </div>
            <button style={C.big} disabled={busy} onClick={async () => { setBusy(true); try { downloadPackage(await getClosePackage(result.month.first)) } finally { setBusy(false) } }}>
              {busy ? 'Preparing…' : 'Download'}
            </button>
          </div>
          <button style={{ ...C.link, alignSelf: 'flex-start' }} onClick={onHome}>Done. Back to Books</button>
        </div>
        <div style={{ ...C.card, flex: '0 1 400px', minWidth: 280 }}>
          <div style={C.h}>Set aside from {monthOnly(result.month.name)}</div>
          {(s.skippedBuckets || []).length > 0 && <div style={{ fontSize: 13.5, ...C.muted, marginTop: 8, lineHeight: 1.5 }}>{s.skipped ? 'All skipped' : 'Some skipped'} for {monthOnly(result.month.name)}: the struck-through ones were not added to the set-asides; the figure is what it would have been.</div>}
          {s.tax != null && <SetAsideLine label="Owners' taxes" amount={s.tax} off={(s.skippedBuckets || []).includes('tax')} note={`${s.targets.taxPctOfProfit}% of the ${money(s.basis.profit)} profit, ${money(s.taxEach)} for each of you. Held in savings for the estimated tax payments.`} />}
          <SetAsideLine label="1% for the Planet" amount={s.planet} off={(s.skippedBuckets || []).includes('planet')} note={`${s.targets.planetPctOfCollected}% of the ${money(s.basis.collected)} collected. Give it this month.`} />
          <SetAsideLine label="Savings reserve" amount={s.savings} off={(s.skippedBuckets || []).includes('savings')} note={`${s.targets.savingsPctOfCollected}% of collections, moved from checking to savings. Goal: ${s.targets.savingsGoalMonths} months of running costs.`} />
          <SetAsideLine label="Bonus fund" amount={s.bonus} off={(s.skippedBuckets || []).includes('bonus')} note={`${s.targets.bonusPctOfProfit}% of profit before owner distributions. Counted, not moved: no account yet, so it's tracked inside savings.`} />
          <SetAsideLine label="Investment account" amount={s.investment || null} note="Turns on when the account is opened." last />
        </div>
      </div>
    </div>
  )
}

function SetAsideLine({ label, amount, note, last, off }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: last ? 'none' : '0.5px solid var(--aj-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ ...C.mono, ...(amount == null ? C.muted : {}), textDecoration: off ? 'line-through' : 'none', opacity: off ? 0.5 : 1 }}>{amount == null ? 'Not started' : money(amount)}{off ? ' · skipped' : ''}</span>
      </div>
      <div style={{ fontSize: 12.5, ...C.muted, marginTop: 3, lineHeight: 1.45 }}>{note}</div>
    </div>
  )
}

/** The CPA package as a CSV: every transaction, then the tie-out and the set-asides. */
function downloadPackage(pkg) {
  const q = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  const lines = [['Date', 'Bank account', 'Description', 'Amount', 'Account code', 'Account', 'Project', 'Core id'].join(',')]
  for (const r of pkg.rows) lines.push([r.date, r.account, r.description, r.amount.toFixed(2), r.code, r.codeName, r.project, r.coreId].map(q).join(','))
  lines.push('')
  lines.push('Tie-out')
  for (const t of Object.values(pkg.tie || {})) lines.push([t.name, 'bank ' + t.bank.net.toFixed(2), 'core ' + t.core.net.toFixed(2), 'difference ' + t.difference.toFixed(2)].map(q).join(','))
  if (pkg.explanation) lines.push(q('Closed without tying: ' + pkg.explanation))
  if (pkg.setAside) {
    const s = pkg.setAside
    lines.push('')
    lines.push('Set aside')
    lines.push(['1% for the Planet', s.planet.toFixed(2)].join(','))
    lines.push(['Savings reserve', s.savings.toFixed(2)].join(','))
    lines.push(['Bonus fund', s.bonus.toFixed(2)].join(','))
    lines.push(['Collected', s.basis.collected.toFixed(2)].join(','))
    lines.push(['Spent running the firm', s.basis.spent.toFixed(2)].join(','))
    lines.push(['Owner distributions', s.basis.distributions.toFixed(2)].join(','))
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `Alder James books ${pkg.month.first.slice(0, 7)}.csv`
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 2000)
}
